import { useState, useEffect, useMemo, useRef } from "react";
import { useSearch, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  Shield,
  Plus,
  Loader2,
  Pencil,
  Trash2,
  RotateCw,
  MapPin,
  Building,
  Check,
  X,
  HelpCircle,
  Mail,
  Server,
  Send,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  Lock,
  Sparkles,
  Info,
  Home,
  Inbox,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Palette,
  Upload,
  Image,
  Settings as SettingsIcon,
  PawPrint,
  Search,
  ExternalLink,
  FileText,
  ShieldAlert,
  ClipboardList,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useScrollHighlight } from "@/hooks/use-scroll-highlight";
import { useAuth } from "@/lib/auth-context";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { CapacitySnapshotSettingsCard } from "@/components/admin/capacity-snapshot-settings";
import { BehaviorSnapshotSettingsCard } from "@/components/admin/behavior-snapshot-settings";
import { AnimalProfilesSettingsCard } from "@/components/admin/animal-profiles-settings";
import { AnimalEvaluationTracksSettingsCard } from "@/components/admin/animal-evaluation-tracks-settings";
import { HubShadowResultsCard } from "@/components/admin/hub-shadow-results";
import { usePageTitle } from "@/hooks/use-page-title";
import { KeywordFlagGuidanceDialog } from "@/components/behavior/KeywordFlagGuidanceDialog";

const LOCATIONS = ["SALEM", "BOSTON", "METHUEN", "CAPE"] as const;
type Location = typeof LOCATIONS[number];

const LOCATION_LABELS: Record<Location, string> = {
  SALEM: "Salem",
  BOSTON: "Boston",
  METHUEN: "Methuen",
  CAPE: "Cape",
};

interface Zone {
  id: string;
  zoneCode: string;
  areaLabel: string;
  location: string;
  locationId: string | null;
  locationLabel: string | null;
  runs: number;
  stainless: number;
  variety: number;
  other: number;
  totalCapacity: number;
  createdAt: string;
  updatedAt: string;
  aliasCount: number;
}

interface ZoneWithAliases extends Zone {
  aliases: Alias[];
}

interface Alias {
  id: string;
  aliasCode: string;
  zoneCode: string;
  createdAt: string;
}

interface ZonesResponse {
  zones: Zone[];
}

interface ShelterLocation {
  id: string;
  code: string;
  label: string;
  isActive: boolean;
  zoneCount: number;
  createdAt: string;
  updatedAt: string;
}

interface EmailSettings {
  configured: boolean;
  isProduction: boolean;
  safetyNote: string;
}

interface SystemInfo {
  version: string;
  buildHash: string;
  environment: string;
  securityMode: string;
  apiReachable: boolean;
  database: {
    healthy: boolean;
    error: string | null;
  };
  email: {
    configured: boolean;
    isProduction: boolean;
  };
  timestamp: string;
}

interface RateLimitEntry {
  type: string;
  key: string;
  lockedUntil: string;
  remainingSeconds: number;
}

interface RateLimitsResponse {
  securityMode: string;
  lockedCount: number;
  lockedEntries: RateLimitEntry[];
  rateLimitConfigs: Record<string, {
    maxAttempts: number;
    windowMinutes: number;
    lockoutMinutes: number;
  }>;
}

interface TestEmailResult {
  result: "sent" | "stubbed" | "blocked";
  message: string;
  reason?: string;
  messageId?: string;
}

interface AppSettingsData {
  appName: string;
  tabTitle: string;
  logoProvider: string;
  logoKey: string | null;
  logoUrl: string;
  metaDescription: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImageKey: string | null;
  robotsNoindexEnabled: boolean;
  themeBgLight: string | null;
  themeCardLight: string | null;
  themeBgDark: string | null;
  themeCardDark: string | null;
  ingestHoldEnabled: boolean;
  ingestHoldReason: string | null;
  ingestAcceptanceDisabled: boolean;
  updatedAt: string;
  updatedByUserId: string | null;
}

interface AiSettings {
  aiSummariesEnabled: boolean;
  openaiApiKeyConfigured: boolean;
  openaiApiKeyType: "project" | "apiKey" | "none";
  openaiModel: string;
  aiTemperature: number;
  allowedModels: string[];
  aiSystemPrompt: string | null;
  aiStylePrompt: string | null;
  restartRequired: boolean;
  encryptionConfigured?: boolean;
}

interface ModelUpdateResult {
  ok: boolean;
  openaiModel: string;
}

interface AiSettingsUpdateResult {
  ok: boolean;
  aiSummariesEnabled: boolean;
  restartRequired: boolean;
}

interface OpenAIKeyUpdateResult {
  ok: boolean;
  openaiApiKeyConfigured: boolean;
  openaiApiKeyType: "project" | "apiKey" | "none";
}

interface PromptsUpdateResult {
  ok: boolean;
  aiSystemPrompt: string | null;
  aiStylePrompt: string | null;
}

const zoneCodePattern = /^[A-Z0-9 ]{2,50}$/;

const zoneFormSchema = z.object({
  zoneCode: z
    .string()
    .min(2, "Kennel code must be at least 2 characters")
    .max(50, "Kennel code must be at most 50 characters")
    .regex(zoneCodePattern, "Use uppercase letters, numbers, and spaces only"),
  areaLabel: z
    .string()
    .min(1, "Area label is required")
    .max(200, "Area label must be at most 200 characters"),
  location: z
    .string()
    .min(2, "Please select a location")
    .max(20, "Location code is too long"),
  runs: z.coerce.number().int().min(0).max(999).default(0),
  stainless: z.coerce.number().int().min(0).max(999).default(0),
  variety: z.coerce.number().int().min(0).max(999).default(0),
  other: z.coerce.number().int().min(0).max(999).default(0),
});

type ZoneFormData = z.infer<typeof zoneFormSchema>;

const aliasFormSchema = z.object({
  aliasCode: z
    .string()
    .min(2, "Alias code must be at least 2 characters")
    .max(50, "Alias code must be at most 50 characters")
    .regex(zoneCodePattern, "Use uppercase letters, numbers, and spaces only"),
  zoneId: z.string().min(1, "Please select a kennel"),
});

type AliasFormData = z.infer<typeof aliasFormSchema>;

const locationCodePattern = /^[A-Z0-9_]{2,20}$/;

const locationFormSchema = z.object({
  code: z
    .string()
    .min(2, "Code must be at least 2 characters")
    .max(20, "Code must be at most 20 characters")
    .regex(locationCodePattern, "Use uppercase letters, numbers, and underscores only"),
  label: z
    .string()
    .min(1, "Label is required")
    .max(100, "Label must be at most 100 characters"),
});

type LocationFormData = z.infer<typeof locationFormSchema>;

function formatDate(dateString: string): string {
  return format(new Date(dateString), "MMM d, yyyy");
}

function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function hslToHex(hsl: string): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) return "#ffffff";
  const h = parseInt(parts[1]) / 360;
  const s = parseInt(parts[2]) / 100;
  const l = parseInt(parts[3]) / 100;
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return `#${Math.round(r * 255).toString(16).padStart(2, "0")}${Math.round(g * 255).toString(16).padStart(2, "0")}${Math.round(b * 255).toString(16).padStart(2, "0")}`;
}

function isValidHsl(val: string): boolean {
  return /^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/.test(val.trim());
}

// ============================================================================
// H4.3: Behavior Alert Thresholds Card Component
// ============================================================================

interface ThresholdConfig {
  defaults: {
    strugglingNowThreshold: number;
    strugglingNowHours: number;
    streakThresholdDays: number;
    spikeMultiplier: number;
    spikeBaselineDays: number;
    trend3dThreshold: number;
    trend3dMinSample: number;
    recentHighSeverityDays: number;
    recentHighSeverityFlagThreshold: number;
  };
  overrides: {
    strugglingNowCount: number | null;
    strugglingNowHours: number | null;
    trend3dThreshold: number | null;
    trend3dMinSample: number | null;
    recentHighSeverityThreshold: number | null;
    recentHighSeverityDays: number | null;
    streakDaysThreshold: number | null;
    spikeMultiplier: number | null;
    spikeBaselineDays: number | null;
  } | null;
  effective: {
    strugglingNowCount: number;
    strugglingNowHours: number;
    trend3dThreshold: number;
    trend3dMinSample: number;
    recentHighSeverityThreshold: number;
    recentHighSeverityDays: number;
    streakDaysThreshold: number;
    spikeMultiplier: number;
    spikeBaselineDays: number;
  };
  updatedAt: string | null;
  updatedBy: string | null;
}

interface RecomputeResult {
  success: boolean;
  animalsProcessed: number;
  errors: Array<{ animalId: string; error: string }>;
  startedAt: string;
  completedAt: string;
  durationMs: number;
}

interface FlagSettingsData {
  keywordList: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface LocationThresholdRow {
  locationCode: string;
  longNoteCharThreshold: number;
  overdueInteractionDays: number;
  updatedAt: string;
  updatedBy: string | null;
}

const THRESHOLD_FIELDS = [
  { 
    key: "strugglingNowCount", 
    label: "Struggling Now Count", 
    description: "Number of high-severity interactions (red/orange scores) within the time window that triggers the 'Struggling Now' signal. Example: If set to 2, an animal with 2+ high-severity interactions in the last 72 hours will be flagged.", 
    min: 1, max: 20, step: 1 
  },
  { 
    key: "strugglingNowHours", 
    label: "Struggling Now Window", 
    description: "How far back (in hours) to look for high-severity interactions when checking 'Struggling Now'. Example: 72 hours = 3 days. If set to 48, only interactions from the last 2 days count toward the signal.", 
    min: 1, max: 168, step: 1 
  },
  { 
    key: "trend3dThreshold", 
    label: "3-Day Trend Threshold", 
    description: "Minimum average score change between the previous 3 days and the 3 days before that to flag as improving/worsening. Example: 0.20 means a 20% change is needed. Higher values = fewer signals, lower values = more sensitive detection.", 
    min: 0.01, max: 1.0, step: 0.01 
  },
  { 
    key: "trend3dMinSample", 
    label: "3-Day Trend Min Sample", 
    description: "Minimum number of interactions required in each 3-day period to calculate a trend. Example: If set to 2, animals with fewer than 2 interactions in either period won't show a trend signal. Prevents unreliable signals from sparse data.", 
    min: 1, max: 10, step: 1 
  },
  { 
    key: "recentHighSeverityThreshold", 
    label: "Recent High Severity Count", 
    description: "Number of high-severity interactions within the lookback period to flag for attention. Example: If set to 3 with 7 days lookback, an animal with 3+ red/orange scores in the past week gets flagged.", 
    min: 1, max: 50, step: 1 
  },
  { 
    key: "recentHighSeverityDays", 
    label: "Recent High Severity Days", 
    description: "How many days back to look when counting recent high-severity interactions. Example: 7 days means only the past week's interactions count toward the threshold.", 
    min: 1, max: 30, step: 1 
  },
  { 
    key: "streakDaysThreshold", 
    label: "Streak Days Threshold", 
    description: "Number of consecutive days with at least one high-severity interaction to trigger a streak signal. Example: If set to 3, an animal needs red/orange scores on 3 days in a row. Identifies persistent issues.", 
    min: 2, max: 14, step: 1 
  },
  { 
    key: "spikeMultiplier", 
    label: "Spike Multiplier", 
    description: "How many times higher today's interaction count must be compared to the baseline average to trigger a spike signal. Example: 2.0 means today's count must be 2x the average. If baseline is 3/day, would need 6+ interactions today.", 
    min: 1.1, max: 10.0, step: 0.1 
  },
  { 
    key: "spikeBaselineDays", 
    label: "Spike Baseline Days", 
    description: "Number of days used to calculate the average baseline for spike detection. Example: 7 days means we average the past week's daily interaction counts, then compare today against that average.", 
    min: 3, max: 30, step: 1 
  },
] as const;

function BehaviorThresholdsCard() {
  const { toast } = useToast();
  const [editedValues, setEditedValues] = useState<Record<string, number | null>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const { data: thresholdConfig, isLoading, isError, refetch } = useQuery<ThresholdConfig>({
    queryKey: ["/api/admin/behavior-alert-thresholds"],
  });

  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, number | null>) => {
      return await apiRequest("PUT", "/api/admin/behavior-alert-thresholds", values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/behavior-alert-thresholds"] });
      setEditedValues({});
      setHasChanges(false);
      toast({ title: "Thresholds saved", description: "Run recompute to apply changes to alert flags." });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to save", description: error.message, variant: "destructive" });
    },
  });

  const recomputeMutation = useMutation<RecomputeResult>({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/admin/behavior-alert-thresholds/recompute", { scope: "all" });
    },
    onSuccess: (result) => {
      toast({
        title: "Recompute complete",
        description: `Processed ${result.animalsProcessed} animals in ${result.durationMs}ms`,
      });
    },
    onError: (error: Error) => {
      toast({ title: "Recompute failed", description: error.message, variant: "destructive" });
    },
  });

  const handleValueChange = (key: string, value: string) => {
    const numValue = value === "" ? null : parseFloat(value);
    setEditedValues(prev => ({ ...prev, [key]: numValue }));
    setHasChanges(true);
  };

  const handleReset = (key: string) => {
    setEditedValues(prev => ({ ...prev, [key]: null }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // H4.3 Fix: Only send fields that were explicitly edited (not undefined)
    // Backend will merge with existing overrides and preserve untouched fields
    const updatePayload: Record<string, number | null> = {};
    for (const field of THRESHOLD_FIELDS) {
      if (editedValues[field.key] !== undefined) {
        updatePayload[field.key] = editedValues[field.key];
      }
      // Don't include unedited fields - backend preserves existing overrides
    }
    saveMutation.mutate(updatePayload);
  };

  const getFieldValue = (key: string): number | null => {
    if (editedValues[key] !== undefined) return editedValues[key];
    if (thresholdConfig?.overrides) {
      return thresholdConfig.overrides[key as keyof typeof thresholdConfig.overrides] ?? null;
    }
    return null;
  };

  const getEffectiveValue = (key: string): number => {
    if (thresholdConfig?.effective) {
      return thresholdConfig.effective[key as keyof typeof thresholdConfig.effective] ?? 0;
    }
    return 0;
  };

  const getDefaultValue = (key: string): number => {
    if (thresholdConfig?.defaults) {
      return thresholdConfig.defaults[key as keyof typeof thresholdConfig.defaults] ?? 0;
    }
    return 0;
  };

  const isOverridden = (key: string): boolean => {
    const overrideValue = getFieldValue(key);
    if (overrideValue === null) return false;
    // Only show "Custom" if the override differs from the default value
    const defaultValue = getDefaultValue(key);
    return overrideValue !== defaultValue;
  };

  if (isLoading) {
    return (
      <Card data-testid="card-behavior-thresholds">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Signal Thresholds
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="card-behavior-thresholds">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Signal Thresholds
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-muted-foreground mb-4">Failed to load thresholds</p>
            <Button onClick={() => refetch()} variant="outline" data-testid="button-retry-thresholds">
              <RotateCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-behavior-thresholds">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Signal Thresholds
        </CardTitle>
        <CardDescription>
          Configure thresholds for signal detection. Changes require recompute to take effect.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4">
          {THRESHOLD_FIELDS.map((field) => {
            const currentValue = getFieldValue(field.key);
            const effectiveValue = getEffectiveValue(field.key);
            const defaultValue = getDefaultValue(field.key);
            const overridden = isOverridden(field.key);

            return (
              <div key={field.key} className="border rounded-lg p-3 space-y-3" data-testid={`row-threshold-${field.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Label className="text-sm font-medium">{field.label}</Label>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-sm">
                          <p className="text-sm">{field.description}</p>
                        </TooltipContent>
                      </Tooltip>
                      {overridden && (
                        <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                          Custom
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Default: {field.step < 1 ? defaultValue.toFixed(2) : defaultValue}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleReset(field.key)}
                    disabled={!overridden}
                    className="shrink-0"
                    data-testid={`button-reset-${field.key}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={currentValue ?? ""}
                    onChange={(e) => handleValueChange(field.key, e.target.value)}
                    placeholder={String(effectiveValue)}
                    className="flex-1"
                    data-testid={`input-threshold-${field.key}`}
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    Effective: {field.step < 1 ? effectiveValue.toFixed(2) : effectiveValue}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              className="w-full sm:w-auto"
              data-testid="button-save-thresholds"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
            {thresholdConfig?.updatedAt && (
              <span className="text-xs text-muted-foreground text-center sm:text-left" data-testid="text-thresholds-updated">
                Last updated: {format(new Date(thresholdConfig.updatedAt), "MMM d, yyyy h:mm a")}
              </span>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => recomputeMutation.mutate()}
            disabled={recomputeMutation.isPending}
            className="w-full sm:w-auto"
            data-testid="button-recompute-all"
          >
            {recomputeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <RotateCw className="h-4 w-4 mr-2" />
            Recompute All
          </Button>
        </div>

        <div className="rounded-lg border p-4 bg-muted/50">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground" data-testid="text-thresholds-info">
              Saving thresholds does NOT automatically recompute signals. Click "Recompute All" to apply new thresholds to existing data.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BehaviorFlagSettingsCard() {
  const { toast } = useToast();
  const [editedKeywords, setEditedKeywords] = useState<string | null>(null);
  const [keywordsHasChanges, setKeywordsHasChanges] = useState(false);
  const [editedLocations, setEditedLocations] = useState<
    Record<string, { longNoteCharThreshold?: number; overdueInteractionDays?: number }>
  >({});
  const [locationsHasChanges, setLocationsHasChanges] = useState(false);

  const {
    data: flagSettingsData,
    isLoading: flagSettingsLoading,
    isError: flagSettingsError,
    refetch: refetchFlagSettings,
  } = useQuery<FlagSettingsData>({
    queryKey: ["/api/admin/behavior-flag-settings"],
  });

  const {
    data: thresholdsData,
    isLoading: thresholdsLoading,
    isError: thresholdsError,
    refetch: refetchThresholds,
  } = useQuery<LocationThresholdRow[]>({
    queryKey: ["/api/admin/behavior-location-thresholds"],
  });

  const keywordMutation = useMutation({
    mutationFn: async (keywordList: string) => {
      return await apiRequest("PUT", "/api/admin/behavior-flag-settings", { keywordList });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/behavior-flag-settings"] });
      setEditedKeywords(null);
      setKeywordsHasChanges(false);
      toast({ title: "Keywords saved", description: "New keywords will apply to future interactions." });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to save keywords", description: error.message, variant: "destructive" });
    },
  });

  const locationMutation = useMutation({
    mutationFn: async (
      edits: Record<string, { longNoteCharThreshold?: number; overdueInteractionDays?: number }>
    ) => {
      await Promise.all(
        Object.entries(edits).map(([locationCode, values]) =>
          apiRequest("PUT", "/api/admin/behavior-location-thresholds", { locationCode, ...values })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/behavior-location-thresholds"] });
      setEditedLocations({});
      setLocationsHasChanges(false);
      toast({ title: "Location thresholds saved", description: "Run recompute to apply to existing data." });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to save thresholds", description: error.message, variant: "destructive" });
    },
  });

  const getKeywordValue = () => {
    if (editedKeywords !== null) return editedKeywords;
    return flagSettingsData?.keywordList ?? "";
  };

  const handleLocationChange = (
    locationCode: string,
    field: "longNoteCharThreshold" | "overdueInteractionDays",
    value: string
  ) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;
    setEditedLocations((prev) => ({
      ...prev,
      [locationCode]: { ...prev[locationCode], [field]: numValue },
    }));
    setLocationsHasChanges(true);
  };

  const mostRecentLocationUpdate =
    thresholdsData && thresholdsData.length > 0
      ? thresholdsData.reduce(
          (latest, r) => (r.updatedAt > latest ? r.updatedAt : latest),
          thresholdsData[0].updatedAt
        )
      : null;

  const isLoading = flagSettingsLoading || thresholdsLoading;
  const isError = flagSettingsError || thresholdsError;

  const handleRetry = () => {
    void refetchFlagSettings();
    void refetchThresholds();
  };

  if (isLoading) {
    return (
      <Card data-testid="card-behavior-flag-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            Flag Detection Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-32 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="card-behavior-flag-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            Flag Detection Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-muted-foreground mb-4">Failed to load flag settings</p>
            <Button onClick={handleRetry} variant="outline" data-testid="button-retry-flag-settings">
              <RotateCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-behavior-flag-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5" />
          Flag Detection Settings
        </CardTitle>
        <CardDescription>
          Configure keyword flags and per-location thresholds for interaction flag detection.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Keyword list */}
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-1.5">
              <Label htmlFor="textarea-flag-keywords" className="text-sm font-medium">
                Flagged Keywords
              </Label>
              <KeywordFlagGuidanceDialog />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Comma-separated list of terms. Interactions containing any of these words (case-insensitive,
              whole-word match) will be flagged. Leave empty to disable keyword flagging.
            </p>
          </div>
          <Textarea
            id="textarea-flag-keywords"
            rows={3}
            value={getKeywordValue()}
            onChange={(e) => {
              setEditedKeywords(e.target.value);
              setKeywordsHasChanges(true);
            }}
            placeholder="e.g. redirect, bite, scratch, lunge"
            data-testid="textarea-flag-keywords"
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => keywordMutation.mutate(getKeywordValue())}
              disabled={!keywordsHasChanges || keywordMutation.isPending}
              data-testid="button-save-flag-keywords"
            >
              {keywordMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Keywords
            </Button>
            {flagSettingsData?.updatedAt && (
              <span className="text-xs text-muted-foreground" data-testid="text-flag-keywords-updated">
                Last updated: {format(new Date(flagSettingsData.updatedAt), "MMM d, yyyy h:mm a")}
              </span>
            )}
          </div>
        </div>

        <div className="border-t" />

        {/* Per-location thresholds */}
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium">Per-Location Thresholds</p>
            <p className="text-xs text-muted-foreground mt-1">
              Long Note Threshold: minimum character count to flag an interaction note.
              Overdue Days: days since last interaction before an animal is considered overdue.
            </p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location</TableHead>
                <TableHead>Long Note Threshold (chars)</TableHead>
                <TableHead>Overdue Days</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(thresholdsData ?? []).map((row) => (
                <TableRow
                  key={row.locationCode}
                  data-testid={`row-location-threshold-${row.locationCode}`}
                >
                  <TableCell className="font-medium">{row.locationCode}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={100}
                      value={
                        editedLocations[row.locationCode]?.longNoteCharThreshold ??
                        row.longNoteCharThreshold
                      }
                      onChange={(e) =>
                        handleLocationChange(row.locationCode, "longNoteCharThreshold", e.target.value)
                      }
                      className="w-28"
                      data-testid={`input-long-note-threshold-${row.locationCode}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={1}
                      value={
                        editedLocations[row.locationCode]?.overdueInteractionDays ??
                        row.overdueInteractionDays
                      }
                      onChange={(e) =>
                        handleLocationChange(row.locationCode, "overdueInteractionDays", e.target.value)
                      }
                      className="w-20"
                      data-testid={`input-overdue-days-${row.locationCode}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => locationMutation.mutate(editedLocations)}
              disabled={!locationsHasChanges || locationMutation.isPending}
              data-testid="button-save-location-thresholds"
            >
              {locationMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Thresholds
            </Button>
            {mostRecentLocationUpdate && (
              <span className="text-xs text-muted-foreground" data-testid="text-location-thresholds-updated">
                Last updated: {format(new Date(mostRecentLocationUpdate), "MMM d, yyyy h:mm a")}
              </span>
            )}
          </div>
        </div>

        {/* Info box */}
        <div className="rounded-lg border p-4 bg-muted/50">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground" data-testid="text-flag-settings-info">
              Use the Recompute All button on the Signal Thresholds card above to apply
              changes to existing interaction data.
            </p>
          </div>
        </div>

      </CardContent>
    </Card>
  );
}

// Gate I9.1.1: Email Ingest Tab Component
interface InboundEmailStatus {
  inboundWebhookEnabled: boolean;
  signingKeyConfigured: boolean;
  environment: string;
  signatureVerificationRequired: boolean;
}

interface InboundEmail {
  id: string;
  provider: string;
  providerMessageId: string;
  receivedAt: string;
  fromAddress: string | null;
  toAddress: string | null;
  subject: string | null;
  classification: string;
  classificationReason: string | null;
  processingStatus: string;
  processingError: string | null;
  processedAt: string | null;
  createdAt: string;
  createdInteractionIds: string[];
  standaloneCount: number;
  normalizedSender: string | null;
  gatewayDecision: string | null;
  gatewayReason: string | null;
  allowlistEnforced: boolean | null;
  rateLimitEnforced: boolean | null;
  duplicateCount: number;
  lastSeenAt: string | null;
}

interface EmailIngestSettingsData {
  allowlistEnabled: boolean;
  allowlistEntries: string[];
  rateLimitEnabled: boolean;
  rateLimitPerSenderPerMinute: number;
}

interface InboundEmailsResponse {
  emails: InboundEmail[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * P28a: Ingest Gate emergency hold control card.
 * Super-admin only — rendered at the top of the Email Ingest tab.
 */
function IngestGateCard() {
  const { toast } = useToast();
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(false);
  const [acceptanceDisabled, setAcceptanceDisabled] = useState(false);

  const { data: settingsData, isLoading } = useQuery<AppSettingsData>({
    queryKey: ["/api/admin/app-settings"],
  });

  const isEnabled = settingsData?.ingestHoldEnabled ?? false;

  useEffect(() => {
    if (settingsData) {
      setReason(settingsData.ingestHoldReason ?? "");
      setAcceptanceDisabled(settingsData.ingestAcceptanceDisabled ?? false);
    }
  }, [settingsData?.ingestHoldReason, settingsData?.ingestAcceptanceDisabled]);

  const mutation = useMutation({
    mutationFn: ({ enabled, reason: r, acceptanceDisabled: ad, _source }: { enabled: boolean; reason: string | null; acceptanceDisabled?: boolean; _source?: "gate" | "acceptance" }) =>
      apiRequest("PUT", "/api/admin/ingest-gate", { enabled, reason: r, acceptanceDisabled: ad ?? false }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app-metadata"] });
      if (vars._source === "acceptance") {
        toast({
          title: vars.acceptanceDisabled ? "Manual acceptance disabled" : "Manual acceptance enabled",
          description: vars.acceptanceDisabled
            ? "Reviewers can no longer accept held reports."
            : "Reviewers can manually accept held reports.",
        });
      } else {
        toast({
          title: vars.enabled ? "Ingest gate enabled" : "Ingest gate disabled",
          description: vars.enabled
            ? "All new PDF reports will be held for manual review."
            : "PDF reports will resume normal processing.",
        });
      }
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update ingest gate.", variant: "destructive" });
    },
  });

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setPendingEnabled(true);
      setConfirmOpen(true);
    } else {
      mutation.mutate({ enabled: false, reason: reason || null, acceptanceDisabled: false, _source: "gate" });
    }
  };

  const handleConfirmEnable = () => {
    setConfirmOpen(false);
    mutation.mutate({ enabled: true, reason: reason || null, acceptanceDisabled, _source: "gate" });
  };

  const handleAcceptanceToggle = (checked: boolean) => {
    setAcceptanceDisabled(checked);
    mutation.mutate({ enabled: true, reason: reason || null, acceptanceDisabled: checked, _source: "acceptance" });
  };

  return (
    <>
      <Card data-testid="card-ingest-gate">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            Ingest Gate — Emergency Hold
          </CardTitle>
          <CardDescription>
            When enabled, all newly ingested PDF reports are held in the review queue regardless of content.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Switch
                  id="ingest-gate-toggle"
                  checked={isEnabled}
                  onCheckedChange={handleToggle}
                  disabled={mutation.isPending}
                  data-testid="switch-ingest-gate"
                />
                <Label htmlFor="ingest-gate-toggle" className="text-sm">
                  {isEnabled ? (
                    <span className="font-medium text-amber-600 dark:text-amber-400">
                      Gate active — all new ingests held for review
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Gate inactive — normal processing</span>
                  )}
                </Label>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ingest-gate-reason" className="text-sm text-muted-foreground">
                  Reason shown in review queue (optional)
                </Label>
                <div className="flex gap-2 items-start">
                  <Textarea
                    id="ingest-gate-reason"
                    rows={2}
                    placeholder="e.g. Pending parser rollout — all reports held for manual review"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={mutation.isPending}
                    data-testid="textarea-ingest-gate-reason"
                    className="flex-1"
                  />
                  {isEnabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => mutation.mutate({ enabled: true, reason: reason || null, acceptanceDisabled })}
                      disabled={
                        mutation.isPending ||
                        reason === (settingsData?.ingestHoldReason ?? "")
                      }
                      data-testid="button-ingest-gate-save-reason"
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>

              {isEnabled && (
                <div className="border-t pt-4 space-y-1.5">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="ingest-acceptance-toggle"
                      checked={acceptanceDisabled}
                      onCheckedChange={handleAcceptanceToggle}
                      disabled={mutation.isPending}
                      data-testid="switch-ingest-acceptance-disabled"
                    />
                    <Label htmlFor="ingest-acceptance-toggle" className="text-sm">
                      {acceptanceDisabled ? (
                        <span className="font-medium text-destructive">
                          Manual acceptance disabled — reviewers cannot accept reports
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          Allow reviewers to manually accept held reports
                        </span>
                      )}
                    </Label>
                    {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  </div>
                  <p className="text-xs text-muted-foreground pl-[calc(2.25rem+0.75rem)]">
                    When disabled, the Accept button is hidden from all reviewers until the gate is lifted.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable ingest gate?</AlertDialogTitle>
            <AlertDialogDescription>
              This will hold <strong>all</strong> newly ingested PDF reports in the review queue until the
              gate is disabled. Existing accepted reports are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingEnabled(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmEnable}
              data-testid="button-ingest-gate-confirm"
            >
              Enable gate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function EmailIngestTab() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);

  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialType = urlParams.get("type") || "all";
  const initialStatus = urlParams.get("status") || "all";
  const initialQualityFlag = urlParams.get("qualityFlag") || "all";
  const highlightEmailId = useMemo(() => urlParams.get("highlight") || null, [urlParams]);
  const highlightFiredRef = useRef(false);

  const [classificationFilter, setClassificationFilter] = useState(initialType);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [qualityFlagFilter, setQualityFlagFilter] = useState(initialQualityFlag);
  const [matchStatusFilter, setMatchStatusFilter] = useState("all");
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);

  useEffect(() => {
    if (initialQualityFlag !== "all") {
      const el = document.getElementById("ingest-log");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const { trigger: triggerHighlight, isHighlighted } = useScrollHighlight({ timeout: 2500 });

  const { data: emailDetail, isLoading: emailDetailLoading } = useQuery<any>({
    queryKey: ["/api/admin/inbound-emails", selectedEmailId],
    queryFn: async () => {
      const res = await fetch(`/api/admin/inbound-emails/${selectedEmailId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch email detail");
      return res.json();
    },
    enabled: !!selectedEmailId,
  });

  const handleClassificationChange = (value: string) => {
    setClassificationFilter(value);
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleQualityFlagChange = (value: string) => {
    setQualityFlagFilter(value);
    setPage(1);
  };

  const handleMatchStatusChange = (value: string) => {
    setMatchStatusFilter(value);
    setPage(1);
  };

  const [newAllowlistEntry, setNewAllowlistEntry] = useState("");

  const { data: statusData, isLoading: isStatusLoading } = useQuery<InboundEmailStatus>({
    queryKey: ["/api/email/inbound/status"],
  });

  const { data: k9Settings, isLoading: isK9Loading } = useQuery<EmailIngestSettingsData>({
    queryKey: ["/api/admin/email-ingest-settings"],
  });

  const k9Mutation = useMutation({
    mutationFn: async (values: Partial<EmailIngestSettingsData>) => {
      return await apiRequest("PUT", "/api/admin/email-ingest-settings", values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-ingest-settings"] });
      toast({ title: "Email ingest settings updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update settings", description: error.message, variant: "destructive" });
    },
  });

  const { data: emailsData, isLoading: isEmailsLoading } = useQuery<InboundEmailsResponse>({
    queryKey: ["/api/admin/inbound-emails", page, classificationFilter, statusFilter, qualityFlagFilter, matchStatusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: "10",
      });
      if (classificationFilter !== "all") {
        params.set("classification", classificationFilter);
      }
      if (statusFilter !== "all") {
        params.set("processingStatus", statusFilter);
      }
      if (qualityFlagFilter !== "all") {
        params.set("qualityFlag", qualityFlagFilter);
      }
      if (matchStatusFilter !== "all") {
        params.set("matchStatus", matchStatusFilter);
      }
      const response = await fetch(`/api/admin/inbound-emails?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch inbound emails");
      }
      return response.json();
    },
  });

  useEffect(() => {
    if (!highlightEmailId || highlightFiredRef.current || !emailsData) return;
    highlightFiredRef.current = true;
    triggerHighlight(highlightEmailId, `row-email-${highlightEmailId}`);
  }, [emailsData, highlightEmailId, triggerHighlight]);

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), "MMM d, yyyy h:mm a");
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "processed":
        return "bg-success/10 text-success border-success/20";
      case "failed":
        return "bg-destructive/10 text-destructive border-destructive/20";
      case "needs_review":
        return "bg-warning/10 text-warning border-warning/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getClassificationLabel = (classification: string) => {
    switch (classification) {
      case "realtime_behavior_alert":
        return "Behavior Alert";
      case "daily_pdf_all_pets":
        return "All Pets PDF";
      case "daily_pdf_interactions":
        return "Interactions PDF";
      default:
        return "Unknown";
    }
  };

  const getDecisionBadgeClass = (decision: string | null) => {
    switch (decision) {
      case "accepted":
        return "bg-success/10 text-success border-success/20";
      case "rejected_allowlist":
        return "bg-destructive/10 text-destructive border-destructive/20";
      case "throttled":
        return "bg-warning/10 text-warning border-warning/20";
      case "not_enforced":
        return "bg-muted text-muted-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getDecisionLabel = (decision: string | null) => {
    switch (decision) {
      case "accepted": return "Accepted";
      case "rejected_allowlist": return "Rejected";
      case "throttled": return "Throttled";
      case "not_enforced": return "Not Enforced";
      default: return "—";
    }
  };


  const handleAddAllowlistEntry = () => {
    const entry = newAllowlistEntry.trim().toLowerCase();
    if (!entry) return;
    if (!entry.startsWith("*@") && !entry.includes("@")) {
      toast({ title: "Invalid entry", description: "Must be an email address or domain pattern (*@domain.com)", variant: "destructive" });
      return;
    }
    const currentEntries = (k9Settings?.allowlistEntries ?? []) as string[];
    if (currentEntries.includes(entry)) {
      toast({ title: "Duplicate entry", description: "This entry is already in the allowlist", variant: "destructive" });
      return;
    }
    k9Mutation.mutate({ allowlistEntries: [...currentEntries, entry] });
    setNewAllowlistEntry("");
  };

  const handleRemoveAllowlistEntry = (entry: string) => {
    const currentEntries = (k9Settings?.allowlistEntries ?? []) as string[];
    k9Mutation.mutate({ allowlistEntries: currentEntries.filter(e => e !== entry) });
  };

  return (
    <>
      {/* P28a: Ingest Gate emergency hold control */}
      <IngestGateCard />

      {/* Status Card */}
      <Card data-testid="card-email-ingest-status">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5" />
            Email Ingestion Status
          </CardTitle>
          <CardDescription>
            Real-time status of the inbound email webhook.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isStatusLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : statusData ? (
            <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Webhook Status</p>
                <p className="flex items-center gap-2" data-testid="text-webhook-status">
                  {statusData.inboundWebhookEnabled ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span>Enabled</span>
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 text-destructive" />
                      <span>Disabled</span>
                    </>
                  )}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Signing Key</p>
                <p className="flex items-center gap-2" data-testid="text-signing-key-status">
                  {statusData.signingKeyConfigured ? (
                    <>
                      <Lock className="h-4 w-4 text-success" />
                      <span>Configured</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4 text-warning" />
                      <span>Not Configured</span>
                    </>
                  )}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Environment</p>
                <p data-testid="text-ingest-environment">
                  {statusData.environment === "production" ? "Production" : "Development"}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Signature Verification</p>
                <p data-testid="text-signature-verification">
                  {statusData.signatureVerificationRequired ? "Required" : "Optional"}
                </p>
              </div>
            </div>
            <div className="mt-4 border-t pt-4">
              <p className="text-sm font-medium mb-2">Configured Ingest Addresses</p>
              <div className="grid gap-2 sm:grid-cols-3">
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">Behavior Alerts</p>
                  <p className="text-sm" data-testid="text-ingest-email-alerts">alerts@mail.betiops.com</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">Daily All Pets Report</p>
                  <p className="text-sm" data-testid="text-ingest-email-allpets">all-pets@mail.betiops.com</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">Daily Interactions Summary</p>
                  <p className="text-sm" data-testid="text-ingest-email-interactions">interactions@mail.betiops.com</p>
                </div>
              </div>
            </div>
            </>
          ) : (
            <p className="text-muted-foreground">Failed to load status</p>
          )}
        </CardContent>
      </Card>

      {/* K9 Phase 1: Ingest Controls Card */}
      <Collapsible defaultOpen={false} className="group">
      <Card data-testid="card-email-ingest-controls">
        <CollapsibleTrigger asChild>
        <CardHeader className="cursor-pointer select-none">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Ingest Controls
            <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground font-normal">
              <span className="group-data-[state=open]:hidden">Expand</span>
              <span className="group-data-[state=closed]:hidden">Collapse</span>
              <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=closed]:-rotate-90" />
            </span>
          </CardTitle>
          <CardDescription>
            Configure allowlist and rate limiting for inbound email processing.
          </CardDescription>
          {!isK9Loading && k9Settings && (
            <div className="flex items-center gap-3 flex-wrap mt-4 text-xs text-muted-foreground group-data-[state=open]:hidden" data-testid="text-ingest-controls-summary">
              <span className="flex items-center gap-1">
                Allowlist: <Badge variant={k9Settings.allowlistEnabled ? "default" : "outline"} className="text-xs no-default-hover-elevate no-default-active-elevate">{k9Settings.allowlistEnabled ? "On" : "Off"}</Badge>
              </span>
              <span className="flex items-center gap-1">
                Rate Limit: <Badge variant={k9Settings.rateLimitEnabled ? "default" : "outline"} className="text-xs no-default-hover-elevate no-default-active-elevate">{k9Settings.rateLimitEnabled ? "On" : "Off"}</Badge>
              </span>
              {k9Settings.allowlistEnabled && ((k9Settings.allowlistEntries ?? []) as string[]).length > 0 && (
                <span className="flex items-center gap-1 flex-wrap">
                  {((k9Settings.allowlistEntries ?? []) as string[]).map((entry) => (
                    <Badge key={entry} variant="secondary" className="text-xs no-default-hover-elevate no-default-active-elevate">{entry}</Badge>
                  ))}
                </span>
              )}
            </div>
          )}
        </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
        <CardContent className="space-y-6">
          {isK9Loading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : (
            <>
              {/* Allowlist Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Enforce Allowlist</Label>
                    <p className="text-sm text-muted-foreground">
                      When enabled, only emails from allowlisted senders will be processed.
                    </p>
                  </div>
                  <Switch
                    data-testid="switch-allowlist-enabled"
                    checked={k9Settings?.allowlistEnabled ?? false}
                    disabled={k9Mutation.isPending}
                    onCheckedChange={(checked) => k9Mutation.mutate({ allowlistEnabled: checked })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Allowlist Entries</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="input-allowlist-entry"
                      placeholder="user@example.com or *@domain.com"
                      value={newAllowlistEntry}
                      onChange={(e) => setNewAllowlistEntry(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddAllowlistEntry(); } }}
                    />
                    <Button
                      data-testid="button-add-allowlist"
                      onClick={handleAddAllowlistEntry}
                      disabled={k9Mutation.isPending || !newAllowlistEntry.trim()}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  {((k9Settings?.allowlistEntries ?? []) as string[]).length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {((k9Settings?.allowlistEntries ?? []) as string[]).map((entry) => (
                        <Badge key={entry} variant="secondary" className="gap-1" data-testid={`badge-allowlist-${entry}`}>
                          {entry}
                          <button
                            onClick={() => handleRemoveAllowlistEntry(entry)}
                            className="ml-1 hover:text-destructive"
                            data-testid={`button-remove-allowlist-${entry}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No entries. All senders are allowed when enforcement is off.</p>
                  )}
                </div>
              </div>

              <div className="border-t pt-4" />

              {/* Rate Limiting Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Enforce Rate Limiting</Label>
                    <p className="text-sm text-muted-foreground">
                      When enabled, limits inbound emails per sender per minute.
                    </p>
                  </div>
                  <Switch
                    data-testid="switch-rate-limit-enabled"
                    checked={k9Settings?.rateLimitEnabled ?? false}
                    disabled={k9Mutation.isPending}
                    onCheckedChange={(checked) => k9Mutation.mutate({ rateLimitEnabled: checked })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Max Emails Per Sender Per Minute</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="input-rate-limit-value"
                      type="number"
                      min={1}
                      max={10000}
                      className="w-32"
                      value={k9Settings?.rateLimitPerSenderPerMinute ?? 60}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1 && val <= 10000) {
                          k9Mutation.mutate({ rateLimitPerSenderPerMinute: val });
                        }
                      }}
                      disabled={k9Mutation.isPending}
                    />
                    <span className="text-sm text-muted-foreground">per minute</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
        </CollapsibleContent>
      </Card>
      </Collapsible>

      {/* Ingestion Log Card */}
      <Card id="ingest-log" data-testid="card-email-ingest-log">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle>Ingestion Log</CardTitle>
            <CardDescription>
              Recent inbound emails and their processing status.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={classificationFilter} onValueChange={handleClassificationChange}>
              <SelectTrigger className="w-[160px]" data-testid="select-classification-filter">
                <SelectValue placeholder="Classification" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="realtime_behavior_alert">Behavior Alert</SelectItem>
                <SelectItem value="daily_pdf_all_pets">All Pets PDF</SelectItem>
                <SelectItem value="daily_pdf_interactions">Interactions PDF</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[140px]" data-testid="select-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="processed">Processed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="needs_review">Needs Review</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select value={qualityFlagFilter} onValueChange={handleQualityFlagChange}>
              <SelectTrigger className="w-[160px]" data-testid="select-quality-flag-filter">
                <SelectValue placeholder="Note Quality" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Notes</SelectItem>
                <SelectItem value="note-quality">Note Quality Flags</SelectItem>
                <SelectItem value="data-integrity">Data Integrity Flags</SelectItem>
                <SelectItem value="any">Any Flag</SelectItem>
              </SelectContent>
            </Select>
            <Select value={matchStatusFilter} onValueChange={handleMatchStatusChange}>
              <SelectTrigger className="w-[150px]" data-testid="select-match-status-filter">
                <SelectValue placeholder="Match Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Emails</SelectItem>
                <SelectItem value="standalone">Unmatched Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isEmailsLoading ? (
            <div className="p-6 space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : emailsData && emailsData.emails.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table className="table-zebra" data-testid="table-inbound-emails">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Received</TableHead>
                      <TableHead>Sender</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1 cursor-default">
                              Gateway <HelpCircle className="h-3 w-3 text-muted-foreground" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[200px] text-center">
                            Did the email pass security checks (allowlist, rate limiting)?
                          </TooltipContent>
                        </Tooltip>
                      </TableHead>
                      <TableHead>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1 cursor-default">
                              Processing <HelpCircle className="h-3 w-3 text-muted-foreground" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[200px] text-center">
                            Was the email content successfully parsed into interactions?
                          </TooltipContent>
                        </Tooltip>
                      </TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Last Seen</TableHead>
                      <TableHead>Interactions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailsData.emails.map((email) => (
                      <TableRow
                        key={email.id}
                        id={`row-email-${email.id}`}
                        data-testid={`row-email-${email.id}`}
                        className={`cursor-pointer hover-elevate ${isHighlighted(email.id) ? "ring-2 ring-primary ring-inset bg-primary/5 transition-all" : ""}`}
                        onClick={() => setSelectedEmailId(email.id)}
                      >
                        <TableCell className="whitespace-nowrap">
                          {formatDate(email.receivedAt)}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate" data-testid={`text-sender-${email.id}`} title={email.normalizedSender || email.fromAddress || undefined}>
                          {email.normalizedSender || email.fromAddress || "—"}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={email.subject || undefined}>
                          {email.subject || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {getClassificationLabel(email.classification)}
                          </Badge>
                        </TableCell>
                        <TableCell data-testid={`text-decision-${email.id}`}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {email.gatewayDecision ? (
                              <Badge className={getDecisionBadgeClass(email.gatewayDecision)}>
                                {getDecisionLabel(email.gatewayDecision)}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                            {email.duplicateCount > 0 && (
                              <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400" data-testid={`badge-deduped-${email.id}`}>
                                Deduped x{email.duplicateCount}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeClass(email.processingStatus)}>
                            {email.processingStatus.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[220px] truncate text-sm text-muted-foreground" data-testid={`text-reason-${email.id}`} title={email.gatewayReason || undefined}>
                          {email.gatewayReason || "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground" data-testid={`text-last-seen-${email.id}`}>
                          {email.lastSeenAt && email.duplicateCount > 0
                            ? formatDate(email.lastSeenAt)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {email.createdInteractionIds.length > 0 ? (
                              <span className="text-sm">{email.createdInteractionIds.length} created</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                            {email.standaloneCount > 0 && (
                              <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-400" data-testid={`badge-standalone-${email.id}`}>
                                {email.standaloneCount} unmatched
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination */}
              {emailsData.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-muted-foreground">
                    Page {emailsData.pagination.page} of {emailsData.pagination.totalPages} ({emailsData.pagination.total} total)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="button-prev-page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(emailsData.pagination.totalPages, p + 1))}
                      disabled={page === emailsData.pagination.totalPages}
                      data-testid="button-next-page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-6 text-center text-muted-foreground">
              No inbound emails found matching the current filters.
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selectedEmailId} onOpenChange={(open) => { if (!open) setSelectedEmailId(null); }}>
        <SheetContent className="w-full sm:w-[680px] flex flex-col overflow-hidden" data-testid="sheet-email-detail">
          <SheetHeader className="flex-shrink-0 pb-2 border-b">
            <div className="flex items-start gap-2">
              <Mail className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <SheetTitle className="text-base leading-snug break-words">
                {emailDetailLoading ? "Loading…" : (emailDetail?.subject || "No subject")}
              </SheetTitle>
            </div>
            {emailDetail && (
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <Badge className={getStatusBadgeClass(emailDetail.processingStatus)}>
                  {emailDetail.processingStatus.replace(/_/g, " ")}
                </Badge>
                {emailDetail.gatewayDecision && (
                  <Badge className={getDecisionBadgeClass(emailDetail.gatewayDecision)}>
                    {getDecisionLabel(emailDetail.gatewayDecision)}
                  </Badge>
                )}
              </div>
            )}
          </SheetHeader>

          <div className="flex-1 overflow-y-auto py-4 min-h-0 space-y-5">
            {emailDetailLoading && (
              <div className="space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-20 w-full" />
              </div>
            )}

            {emailDetail && !emailDetailLoading && (
              <>
                {emailDetail.processingError && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-xs font-medium text-destructive mb-1 uppercase tracking-wider">Processing Error</p>
                    <p className="text-sm text-destructive/90 whitespace-pre-wrap break-words">{emailDetail.processingError}</p>
                  </div>
                )}

                {emailDetail.gatewayReason && (
                  <div className="rounded-md border border-amber-400/30 bg-amber-50/50 dark:bg-amber-900/10 p-3">
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1 uppercase tracking-wider">Gateway Reason</p>
                    <p className="text-sm text-amber-900 dark:text-amber-300 break-words">{emailDetail.gatewayReason}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Email Details</p>
                  <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                    <span className="text-muted-foreground">From</span>
                    <span className="break-all">{emailDetail.fromAddress || "—"}</span>
                    <span className="text-muted-foreground">To</span>
                    <span className="break-all">{emailDetail.toAddress || "—"}</span>
                    <span className="text-muted-foreground">Received</span>
                    <span>{formatDate(emailDetail.receivedAt)}</span>
                    {emailDetail.processedAt && (
                      <>
                        <span className="text-muted-foreground">Processed</span>
                        <span>{formatDate(emailDetail.processedAt)}</span>
                      </>
                    )}
                    <span className="text-muted-foreground">Type</span>
                    <span>{getClassificationLabel(emailDetail.classification)}</span>
                    {emailDetail.classificationReason && (
                      <>
                        <span className="text-muted-foreground">Classified as</span>
                        <span className="break-words">{emailDetail.classificationReason}</span>
                      </>
                    )}
                    {emailDetail.duplicateCount > 0 && (
                      <>
                        <span className="text-muted-foreground">Duplicates</span>
                        <span>{emailDetail.duplicateCount} seen again</span>
                      </>
                    )}
                    {(emailDetail.allowlistEnforced !== null || emailDetail.rateLimitEnforced !== null) && (
                      <>
                        <span className="text-muted-foreground">Enforcement</span>
                        <span className="flex gap-1.5 flex-wrap">
                          {emailDetail.allowlistEnforced && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Allowlist</Badge>}
                          {emailDetail.rateLimitEnforced && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Rate Limit</Badge>}
                          {!emailDetail.allowlistEnforced && !emailDetail.rateLimitEnforced && <span className="text-muted-foreground">None</span>}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {emailDetail.linkedInteractions?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Interactions Created ({emailDetail.linkedInteractions.length})
                    </p>
                    <div className="space-y-2">
                      {emailDetail.linkedInteractions.map((interaction: any) => {
                        const flag: string | null = interaction.rawNoteQualityFlag ?? null;
                        const isDataIntegrity = flag === "phantom" || flag === "contaminated-tail" || flag === "cross-animal-bleed";
                        const isNoteQuality = flag === "score-only" || flag === "short-report";
                        const flagLabel: Record<string, string> = {
                          "score-only": "Score Only",
                          "short-report": "Short Note",
                          "phantom": "Phantom",
                          "contaminated-tail": "Contaminated",
                          "cross-animal-bleed": "Cross-Animal",
                        };
                        return (
                          <div key={interaction.id} className="border rounded-md p-3 space-y-2">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                <span className="font-mono text-xs text-muted-foreground">{interaction.animalId}</span>
                                {interaction.scoreCode && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{interaction.scoreCode}</Badge>}
                                {interaction.dedupeStatus === "superseded" && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">superseded</Badge>}
                                {isDataIntegrity && (
                                  <Badge className="text-[10px] px-1.5 py-0 bg-destructive/10 text-destructive border-destructive/30">
                                    {flagLabel[flag!]}
                                  </Badge>
                                )}
                                {isNoteQuality && (
                                  <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300/40">
                                    {flagLabel[flag!]}
                                  </Badge>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {interaction.locationCode} · {format(new Date(interaction.occurredAt), "MMM d")}
                              </span>
                            </div>
                            {interaction.rawNote && (
                              <div className="space-y-1">
                                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Note from email</p>
                                <div className="max-h-32 overflow-y-auto rounded bg-muted/40 px-2 py-1.5">
                                  <p className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
                                    {interaction.rawNote}
                                  </p>
                                </div>
                              </div>
                            )}
                            {interaction.dedupeStatus === "superseded" && interaction.canonicalRawNote !== null && (
                              <div className="space-y-1">
                                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Note from canonical report</p>
                                <div className="max-h-32 overflow-y-auto rounded bg-muted/20 px-2 py-1.5">
                                  <p className="text-xs text-foreground/80 whitespace-pre-wrap break-words">
                                    {interaction.canonicalRawNote || <span className="italic text-muted-foreground">No note on canonical</span>}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {emailDetail.bodyPlain && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <FileText className="h-3 w-3" /> Email Body
                    </p>
                    <div className="max-h-64 overflow-y-auto rounded-md border bg-muted/30 p-3">
                      <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words font-mono leading-relaxed">
                        {emailDetail.bodyPlain.length > 5000
                          ? emailDetail.bodyPlain.slice(0, 5000) + "\n\n[truncated…]"
                          : emailDetail.bodyPlain}
                      </pre>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

interface MfaSettings {
  enforcementPolicy: "optional" | "required_admins" | "required_all";
  rememberDeviceHours: number;
}

const ENFORCEMENT_LABELS: Record<string, string> = {
  optional: "Optional",
  required_admins: "Required for Admins",
  required_all: "Required for All Users",
};

const ENFORCEMENT_SEVERITY: Record<string, number> = {
  optional: 0,
  required_admins: 1,
  required_all: 2,
};

const ENFORCEMENT_CONSEQUENCES: Record<string, string> = {
  required_admins: "All admin and super admin users will be required to set up two-factor authentication before accessing the application. Any admin who has not yet enrolled will be prompted to do so on their next sign-in.",
  required_all: "Every user in the system will be required to set up two-factor authentication before accessing the application. Anyone who has not yet enrolled will be prompted to do so on their next sign-in.",
};

interface BreedMapping {
  id: string;
  breed: string;
  species: string;
  source: string;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

const SPECIES_OPTIONS = [
  { value: "DOG", label: "Dog" },
  { value: "CAT", label: "Cat" },
  { value: "OTHER", label: "Other" },
] as const;

function BreedMappingsCard() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [speciesFilter, setSpeciesFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [addingBreed, setAddingBreed] = useState(false);
  const [newBreed, setNewBreed] = useState("");
  const [newSpecies, setNewSpecies] = useState<string>("DOG");

  const { data, isLoading } = useQuery<{ items: BreedMapping[] }>({
    queryKey: ["/api/breed-mappings", searchQuery, speciesFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.set("search", searchQuery);
      if (speciesFilter !== "all") params.set("species", speciesFilter);
      const qs = params.toString();
      return apiRequest("GET", `/api/breed-mappings${qs ? `?${qs}` : ""}`);
    },
  });

  const allItems = data?.items || [];
  const availableSources = [...new Set(allItems.map(i => i.source))].sort();
  const items = sourceFilter === "all" ? allItems : allItems.filter(i => i.source === sourceFilter);

  const updateMutation = useMutation({
    mutationFn: async ({ id, species }: { id: string; species: string }) => {
      return apiRequest("PATCH", `/api/breed-mappings/${id}`, { species });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/breed-mappings"] });
      toast({ title: "Breed mapping updated" });
    },
    onError: () => {
      toast({ title: "Failed to update breed mapping", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/breed-mappings/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/breed-mappings"] });
      setDeletingId(null);
      toast({ title: "Breed mapping deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete breed mapping", variant: "destructive" });
    },
  });

  const createMutation = useMutation({
    mutationFn: async ({ breed, species }: { breed: string; species: string }) => {
      return apiRequest("POST", "/api/breed-mappings", { breed, species });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/breed-mappings"] });
      setAddingBreed(false);
      setNewBreed("");
      setNewSpecies("DOG");
      toast({ title: "Breed mapping added" });
    },
    onError: (error: any) => {
      const message = error?.message?.includes("already exists")
        ? "This breed already exists"
        : "Failed to add breed mapping";
      toast({ title: message, variant: "destructive" });
    },
  });

  return (
    <Card data-testid="card-breeds">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <CardTitle>Breed Mappings</CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="inline-flex" data-testid="tooltip-breed-species-info">
                  <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p>Dog and Cat keywords tell the system which species to assign when parsing reports. Other keywords (small animals, reptiles, livestock, etc.) act as a blocklist — when one appears in a breed field, the system will not guess Dog or Cat, leaving species for manual review.</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <CardDescription>
            Manage breed-to-species mappings used for automatic species inference during report parsing.
            Mappings are auto-learned when staff correct species during report review.
          </CardDescription>
        </div>
        <Button
          onClick={() => setAddingBreed(true)}
          data-testid="button-add-breed"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Breed
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search breeds..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="input-breed-search"
            />
          </div>
          <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
            <SelectTrigger className="w-[140px]" data-testid="select-species-filter">
              <SelectValue placeholder="Species" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Species</SelectItem>
              {SPECIES_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-source-filter">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {availableSources.map((src) => (
                <SelectItem key={src} value={src}>
                  {src === "manual" ? "User (Settings)"
                    : src === "review" ? "User (Review)"
                    : src === "learned" ? "System (Learned)"
                    : "System"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {addingBreed && (
          <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/50">
            <Input
              placeholder="Breed keyword (e.g. LABRADOR)"
              value={newBreed}
              onChange={(e) => setNewBreed(e.target.value)}
              className="flex-1"
              maxLength={200}
              data-testid="input-new-breed"
            />
            <Select value={newSpecies} onValueChange={setNewSpecies}>
              <SelectTrigger className="w-[130px]" data-testid="select-new-species">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPECIES_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="icon"
              onClick={() => {
                if (newBreed.trim()) {
                  createMutation.mutate({ breed: newBreed.trim(), species: newSpecies });
                }
              }}
              disabled={!newBreed.trim() || createMutation.isPending}
              data-testid="button-confirm-add-breed"
            >
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => { setAddingBreed(false); setNewBreed(""); }}
              data-testid="button-cancel-add-breed"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="text-no-breeds">
            {searchQuery ? "No breeds match your search." : "No breed mappings found."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Breed Keyword</TableHead>
                <TableHead>Species</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid={`row-breed-${item.id}`}>
                  <TableCell className="font-medium" data-testid={`text-breed-${item.id}`}>
                    {item.breed}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={item.species}
                      onValueChange={(value) => updateMutation.mutate({ id: item.id, species: value })}
                    >
                      <SelectTrigger className="w-[130px]" data-testid={`select-species-${item.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SPECIES_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs" data-testid={`badge-source-${item.id}`}>
                      {item.source === "manual"
                        ? (item.createdByName ? `${item.createdByName.trim()} (Settings)` : "User (Settings)")
                        : item.source === "review"
                        ? (item.createdByName ? `${item.createdByName.trim()} (Review)` : "User (Review)")
                        : item.source === "learned"
                        ? "System (Learned)"
                        : "System"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm" data-testid={`text-date-${item.id}`}>
                    {format(new Date(item.createdAt), "MMM d, yyyy h:mm a")}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeletingId(item.id)}
                      data-testid={`button-delete-breed-${item.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div className="text-xs text-muted-foreground" data-testid="text-breed-count">
          {items.length} breed mapping{items.length !== 1 ? "s" : ""}
        </div>
      </CardContent>

      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Breed Mapping</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this breed mapping? The system will no longer automatically
              infer species from this breed keyword.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-breed">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingId && deleteMutation.mutate(deletingId)}
              data-testid="button-confirm-delete-breed"
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function MfaSettingsCard() {
  const { toast } = useToast();
  const [resetType, setResetType] = useState<"device_trust" | "full" | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [pendingPolicy, setPendingPolicy] = useState<MfaSettings["enforcementPolicy"] | null>(null);

  const { data: mfaSettings, isLoading } = useQuery<MfaSettings>({
    queryKey: ["/api/admin/mfa-settings"],
  });

  const updateMutation = useMutation({
    mutationFn: async (values: Partial<MfaSettings>) => {
      return await apiRequest("PUT", "/api/admin/mfa-settings", values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/mfa-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "MFA settings updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async (body: { resetType: string; confirmation: string }) => {
      return await apiRequest("POST", "/api/admin/mfa-reset", body);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/mfa-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      setResetType(null);
      setConfirmText("");
      const label = variables.resetType === "full"
        ? "All TOTP secrets and trusted devices have been cleared."
        : "All trusted devices have been cleared.";
      toast({ title: "MFA reset complete", description: label });
    },
    onError: (error: Error) => {
      toast({ title: "Reset failed", description: error.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <Card data-testid="card-mfa-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentPolicy = mfaSettings?.enforcementPolicy ?? "optional";
  const currentHours = mfaSettings?.rememberDeviceHours ?? 720;

  return (
    <>
      <Card data-testid="card-mfa-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Configure MFA enforcement policy and device trust settings for the organization.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border bg-muted/50 p-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="mfa-policy-summary">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Current policy:</span>
              <Badge variant={currentPolicy === "optional" ? "secondary" : "default"} data-testid="badge-current-policy">
                {ENFORCEMENT_LABELS[currentPolicy] ?? currentPolicy}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Device trust:</span>
              <span className="text-sm font-medium" data-testid="text-device-trust-summary">
                {currentHours} hours ({Math.round(currentHours / 24)} days)
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Enforcement Policy</Label>
            <Select
              value={currentPolicy}
              onValueChange={(value: string) => {
                const newPolicy = value as MfaSettings["enforcementPolicy"];
                const isEscalation = (ENFORCEMENT_SEVERITY[newPolicy] ?? 0) > (ENFORCEMENT_SEVERITY[currentPolicy] ?? 0);
                if (isEscalation) {
                  setPendingPolicy(newPolicy);
                } else {
                  updateMutation.mutate({ enforcementPolicy: newPolicy });
                }
              }}
              disabled={updateMutation.isPending}
            >
              <SelectTrigger data-testid="select-mfa-policy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="optional">Optional</SelectItem>
                <SelectItem value="required_admins">Required for Admins</SelectItem>
                <SelectItem value="required_all">Required for All Users</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {currentPolicy === "optional" && "Users may choose whether to enable two-factor authentication."}
              {currentPolicy === "required_admins" && "Admin users must set up two-factor authentication before accessing the application. Staff users may optionally enable it."}
              {currentPolicy === "required_all" && "All users must set up two-factor authentication before accessing the application."}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Remember Device Duration (hours)</Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                max={8760}
                value={currentHours}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 1 && val <= 8760) {
                    updateMutation.mutate({ rememberDeviceHours: val });
                  }
                }}
                className="w-32"
                data-testid="input-remember-hours"
              />
              <span className="text-sm text-muted-foreground">
                ({Math.round(currentHours / 24)} days)
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              How long a device is trusted after a user checks "Remember this device" during TOTP verification.
            </p>
          </div>

          <div className="border-t pt-6 space-y-3">
            <Label className="text-destructive">System-Wide Reset</Label>
            <p className="text-sm text-muted-foreground">
              These actions affect all users in the organization. Use with extreme caution.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => { setResetType("device_trust"); setConfirmText(""); }}
                data-testid="button-reset-devices"
              >
                <RotateCw className="h-4 w-4 mr-2" />
                Clear All Trusted Devices
              </Button>
              <Button
                variant="outline"
                onClick={() => { setResetType("full"); setConfirmText(""); }}
                data-testid="button-reset-full"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Full MFA Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={resetType !== null} onOpenChange={(open) => { if (!open) { setResetType(null); setConfirmText(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {resetType === "full" ? "Full MFA Reset" : "Clear Trusted Devices"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {resetType === "full"
                ? "This will disable TOTP for ALL users, delete all TOTP secrets, and clear all trusted devices. Every user will need to re-enroll in two-factor authentication."
                : "This will clear all trusted devices for every user. Users will be prompted for their TOTP code on their next login."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label>Type RESET to confirm</Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="RESET"
              data-testid="input-reset-confirm"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (resetType) {
                  resetMutation.mutate({ resetType, confirmation: confirmText });
                }
              }}
              disabled={confirmText !== "RESET" || resetMutation.isPending}
              className="bg-destructive text-destructive-foreground"
              data-testid="button-reset-confirm"
            >
              {resetMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {resetType === "full" ? "Reset All MFA" : "Clear Devices"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingPolicy !== null} onOpenChange={(open) => { if (!open) setPendingPolicy(null); }}>
        <AlertDialogContent data-testid="dialog-policy-escalation">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Policy Change
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are changing the MFA enforcement policy from <strong>{ENFORCEMENT_LABELS[currentPolicy] ?? currentPolicy}</strong> to <strong>{pendingPolicy ? (ENFORCEMENT_LABELS[pendingPolicy] ?? pendingPolicy) : ""}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground">
              {pendingPolicy ? ENFORCEMENT_CONSEQUENCES[pendingPolicy] : ""}
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setPendingPolicy(null)}
              data-testid="button-policy-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingPolicy) {
                  updateMutation.mutate({ enforcementPolicy: pendingPolicy });
                  setPendingPolicy(null);
                }
              }}
              disabled={updateMutation.isPending}
              data-testid="button-policy-confirm"
            >
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function Settings() {
  usePageTitle("Settings");
  const { user: currentUser, isLoading: isAuthLoading } = useAuth();
  const { toast } = useToast();

  const [isZoneDrawerOpen, setIsZoneDrawerOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [deletingZone, setDeletingZone] = useState<Zone | null>(null);
  const [isAliasDialogOpen, setIsAliasDialogOpen] = useState(false);
  const [deletingAlias, setDeletingAlias] = useState<Alias | null>(null);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<ShelterLocation | null>(null);
  const [deactivatingLocation, setDeactivatingLocation] = useState<ShelterLocation | null>(null);

  const searchString = useSearch();
  const [, setLocation] = useLocation();
  
  const validTabs = ["zones", "locations", "email", "email-ingest", "system", "ai", "behavior", "animal-profiles", "snapshots", "hub-shadow", "security", "breeds"] as const;
  type SettingsTab = typeof validTabs[number];
  const superAdminOnlyTabs: SettingsTab[] = ["ai", "email-ingest", "hub-shadow", "security"];
  
  const tabFromUrl = useMemo(() => {
    const params = new URLSearchParams(searchString);
    const tabParam = params.get("tab");
    if (tabParam && validTabs.includes(tabParam as SettingsTab)) {
      return tabParam as SettingsTab;
    }
    return "zones";
  }, [searchString]);
  
  const [activeTab, setActiveTab] = useState<SettingsTab>(tabFromUrl);
  
  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);
  
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab as SettingsTab);
    setLocation(`/admin/settings?tab=${newTab}`, { replace: true });
  };

  const zoneForm = useForm<ZoneFormData>({
    resolver: zodResolver(zoneFormSchema),
    defaultValues: {
      zoneCode: "",
      areaLabel: "",
      location: "",
      runs: 0,
      stainless: 0,
      variety: 0,
      other: 0,
    },
  });

  const aliasForm = useForm<AliasFormData>({
    resolver: zodResolver(aliasFormSchema),
    defaultValues: {
      aliasCode: "",
      zoneId: "",
    },
  });

  const locationForm = useForm<LocationFormData>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      code: "",
      label: "",
    },
  });

  const runsValue = zoneForm.watch("runs");
  const stainlessValue = zoneForm.watch("stainless");
  const varietyValue = zoneForm.watch("variety");
  const otherValue = zoneForm.watch("other");
  const totalCapacity = 
    (Number(runsValue) || 0) + 
    (Number(stainlessValue) || 0) + 
    (Number(varietyValue) || 0) + 
    (Number(otherValue) || 0);

  const {
    data: zonesData,
    isLoading: isZonesLoading,
    isError: isZonesError,
    error: zonesError,
    refetch: refetchZones,
  } = useQuery<ZonesResponse>({
    queryKey: ["/api/zones"],
    enabled: !!currentUser?.isAdmin,
  });

  const allAliases: (Alias & { zoneName: string })[] = [];
  const zones = zonesData?.zones || [];

  const {
    data: allZonesWithAliases,
    isLoading: isAliasesLoading,
  } = useQuery({
    queryKey: ["/api/zones/all-aliases"],
    queryFn: async () => {
      const results: (Alias & { zoneName: string })[] = [];
      for (const zone of zones) {
        try {
          const response = await fetch(`/api/zones/${zone.id}/aliases`, {
            credentials: "include",
          });
          if (response.ok) {
            const data = await response.json();
            for (const alias of data.aliases || []) {
              results.push({
                ...alias,
                zoneName: `${zone.zoneCode} – ${zone.areaLabel}`,
              });
            }
          }
        } catch (e) {
          console.error("Failed to fetch aliases for zone", zone.id, e);
        }
      }
      return results;
    },
    enabled: !!currentUser?.isAdmin && zones.length > 0,
  });

  const aliases = allZonesWithAliases || [];

  const {
    data: locationsData,
    isLoading: isLocationsLoading,
    isError: isLocationsError,
    error: locationsError,
    refetch: refetchLocations,
  } = useQuery<ShelterLocation[]>({
    queryKey: ["/api/locations"],
    enabled: !!currentUser?.isAdmin,
  });

  const locationsList = locationsData || [];

  const [testEmail, setTestEmail] = useState("");
  const [testEmailError, setTestEmailError] = useState<string | null>(null);
  const [openaiKeyInput, setOpenaiKeyInput] = useState("");
  const [systemPromptInput, setSystemPromptInput] = useState("");
  const [stylePromptInput, setStylePromptInput] = useState("");
  const [promptsInitialized, setPromptsInitialized] = useState(false);
  const [temperatureValue, setTemperatureValue] = useState<number>(0.7);
  const [temperatureInitialized, setTemperatureInitialized] = useState(false);

  const {
    data: emailSettings,
    isLoading: isEmailSettingsLoading,
  } = useQuery<EmailSettings>({
    queryKey: ["/api/settings/email"],
    enabled: !!currentUser?.isAdmin,
  });

  const {
    data: systemInfo,
    isLoading: isSystemInfoLoading,
    refetch: refetchSystemInfo,
  } = useQuery<SystemInfo>({
    queryKey: ["/api/settings/system-info"],
    enabled: !!currentUser?.isAdmin,
  });

  const {
    data: rateLimits,
    isLoading: isRateLimitsLoading,
    refetch: refetchRateLimits,
  } = useQuery<RateLimitsResponse>({
    queryKey: ["/api/admin/rate-limits"],
    enabled: !!currentUser?.isAdmin,
    refetchInterval: 30000,
  });

  const {
    data: aiSettings,
    isLoading: isAiSettingsLoading,
    isError: isAiSettingsError,
    refetch: refetchAiSettings,
  } = useQuery<AiSettings>({
    queryKey: ["/api/settings/ai"],
    enabled: !!currentUser?.isAdmin,
  });

  const {
    data: appSettingsData,
    isLoading: isAppSettingsLoading,
    refetch: refetchAppSettings,
  } = useQuery<AppSettingsData>({
    queryKey: ["/api/admin/app-settings"],
    enabled: !!currentUser?.isAdmin,
  });

  const [brandingAppName, setBrandingAppName] = useState("");
  const [brandingTabTitle, setBrandingTabTitle] = useState("");
  const [brandingMetaDescription, setBrandingMetaDescription] = useState("");
  const [brandingOgTitle, setBrandingOgTitle] = useState("");
  const [brandingOgDescription, setBrandingOgDescription] = useState("");
  const [brandingRobotsNoindex, setBrandingRobotsNoindex] = useState(true);
  const [brandingLogoPreview, setBrandingLogoPreview] = useState<string | null>(null);
  const [brandingLogoFile, setBrandingLogoFile] = useState<File | null>(null);
  const [brandingInitialized, setBrandingInitialized] = useState(false);
  const [logoTimestamp, setLogoTimestamp] = useState(Date.now());
  const [themeBgLight, setThemeBgLight] = useState<string>("");
  const [themeCardLight, setThemeCardLight] = useState<string>("");
  const [themeBgDark, setThemeBgDark] = useState<string>("");
  const [themeCardDark, setThemeCardDark] = useState<string>("");

  useEffect(() => {
    if (appSettingsData) {
      setBrandingAppName(appSettingsData.appName);
      setBrandingTabTitle(appSettingsData.tabTitle);
      setBrandingMetaDescription(appSettingsData.metaDescription || "");
      setBrandingOgTitle(appSettingsData.ogTitle || "");
      setBrandingOgDescription(appSettingsData.ogDescription || "");
      setBrandingRobotsNoindex(appSettingsData.robotsNoindexEnabled);
      setThemeBgLight(appSettingsData.themeBgLight || "");
      setThemeCardLight(appSettingsData.themeCardLight || "");
      setThemeBgDark(appSettingsData.themeBgDark || "");
      setThemeCardDark(appSettingsData.themeCardDark || "");
      setBrandingInitialized(true);
    }
  }, [appSettingsData]);

  const updateAppSettingsMutation = useMutation({
    mutationFn: async (data: Partial<AppSettingsData>): Promise<{ ok: boolean; settings: AppSettingsData }> => {
      return await apiRequest("PUT", "/api/admin/app-settings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app-metadata"] });
      toast({
        title: "Settings Saved",
        description: "Branding and metadata settings have been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save settings",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File): Promise<{ ok: boolean; logoProvider: string; logoKey: string; logoUrl: string }> => {
      const formData = new FormData();
      formData.append("file", file);
      return await apiRequest("POST", "/api/admin/app-settings/logo", formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app-metadata"] });
      setLogoTimestamp(Date.now());
      setBrandingLogoFile(null);
      setBrandingLogoPreview(null);
      toast({
        title: "Logo Uploaded",
        description: "Your custom logo has been saved.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to upload logo",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetLogoMutation = useMutation({
    mutationFn: async (): Promise<{ ok: boolean; logoProvider: string; logoUrl: string }> => {
      return await apiRequest("DELETE", "/api/admin/app-settings/logo", {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app-metadata"] });
      setLogoTimestamp(Date.now());
      toast({
        title: "Logo Reset",
        description: "Logo has been reset to the default.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to reset logo",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!["image/png", "image/jpeg"].includes(file.type)) {
        toast({
          title: "Invalid File",
          description: "Only PNG and JPEG images are allowed.",
          variant: "destructive",
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Logo must be under 2MB.",
          variant: "destructive",
        });
        return;
      }
      setBrandingLogoFile(file);
      setBrandingLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveBrandingSettings = () => {
    updateAppSettingsMutation.mutate({
      appName: brandingAppName,
      tabTitle: brandingTabTitle,
      metaDescription: brandingMetaDescription || null,
      ogTitle: brandingOgTitle || null,
      ogDescription: brandingOgDescription || null,
      robotsNoindexEnabled: brandingRobotsNoindex,
      themeBgLight: themeBgLight || null,
      themeCardLight: themeCardLight || null,
      themeBgDark: themeBgDark || null,
      themeCardDark: themeCardDark || null,
    });
  };

  const handleResetThemeColors = () => {
    setThemeBgLight("");
    setThemeCardLight("");
    setThemeBgDark("");
    setThemeCardDark("");
    updateAppSettingsMutation.mutate({
      themeBgLight: null,
      themeCardLight: null,
      themeBgDark: null,
      themeCardDark: null,
    });
  };

  const handleUploadLogo = () => {
    if (brandingLogoFile) {
      uploadLogoMutation.mutate(brandingLogoFile);
    }
  };

  const handleResetLogo = () => {
    resetLogoMutation.mutate();
  };

  const validateEmailMutation = useMutation({
    mutationFn: async (email: string): Promise<{ valid: boolean; reason?: string }> => {
      return await apiRequest("POST", "/api/settings/email/validate", { email });
    },
  });

  const testInviteEmailMutation = useMutation({
    mutationFn: async (toEmail: string): Promise<TestEmailResult> => {
      return await apiRequest("POST", "/api/settings/email/test-invite", { toEmail });
    },
    onSuccess: (data) => {
      if (data.result === "sent") {
        toast({ title: "Test Invite Email Sent", description: data.message });
      } else if (data.result === "stubbed") {
        toast({ title: "Email Stubbed", description: data.message, variant: "default" });
      } else {
        toast({ title: "Email Blocked", description: data.message, variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Failed to send test email", description: error.message, variant: "destructive" });
    },
  });

  const testResetEmailMutation = useMutation({
    mutationFn: async (toEmail: string): Promise<TestEmailResult> => {
      return await apiRequest("POST", "/api/settings/email/test-reset", { toEmail });
    },
    onSuccess: (data) => {
      if (data.result === "sent") {
        toast({ title: "Test Reset Email Sent", description: data.message });
      } else if (data.result === "stubbed") {
        toast({ title: "Email Stubbed", description: data.message, variant: "default" });
      } else {
        toast({ title: "Email Blocked", description: data.message, variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Failed to send test email", description: error.message, variant: "destructive" });
    },
  });

  const updateAiSettingsMutation = useMutation({
    mutationFn: async (enabled: boolean): Promise<AiSettingsUpdateResult> => {
      return await apiRequest("POST", "/api/settings/ai", { aiSummariesEnabled: enabled });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/settings/ai"] });
      toast({
        title: "AI Settings Updated",
        description: data.aiSummariesEnabled ? "AI summaries enabled" : "AI summaries disabled",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update AI settings",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateOpenaiKeyMutation = useMutation({
    mutationFn: async (key: string): Promise<OpenAIKeyUpdateResult> => {
      return await apiRequest("POST", "/api/settings/ai/openai-key", { key });
    },
    onSuccess: (data) => {
      setOpenaiKeyInput("");
      queryClient.invalidateQueries({ queryKey: ["/api/settings/ai"] });
      toast({
        title: data.openaiApiKeyConfigured ? "OpenAI Key Saved" : "OpenAI Key Removed",
        description: data.openaiApiKeyConfigured 
          ? `Key configured (${data.openaiApiKeyType === "project" ? "Project key" : "API key"})`
          : "The OpenAI key has been removed",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save OpenAI key",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateModelMutation = useMutation({
    mutationFn: async (model: string): Promise<ModelUpdateResult> => {
      return await apiRequest("POST", "/api/settings/ai/model", { model });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/settings/ai"] });
      toast({
        title: "Model Updated",
        description: `Now using ${data.openaiModel}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update model",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updatePromptsMutation = useMutation({
    mutationFn: async (data: { systemPrompt?: string | null; stylePrompt?: string | null }): Promise<PromptsUpdateResult> => {
      return await apiRequest("POST", "/api/settings/ai/prompts", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/settings/ai"] });
      toast({
        title: "Prompts Saved",
        description: "AI prompt guidance has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save prompts",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateTemperatureMutation = useMutation({
    mutationFn: async (temperature: number): Promise<{ aiTemperature: number }> => {
      return await apiRequest("POST", "/api/settings/ai/temperature", { temperature });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/settings/ai"] });
      toast({
        title: "Temperature Updated",
        description: `Temperature set to ${data.aiTemperature.toFixed(1)}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update temperature",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (aiSettings && !promptsInitialized) {
      setSystemPromptInput(aiSettings.aiSystemPrompt || "");
      setStylePromptInput(aiSettings.aiStylePrompt || "");
      setPromptsInitialized(true);
    }
  }, [aiSettings, promptsInitialized]);

  useEffect(() => {
    if (aiSettings && !temperatureInitialized) {
      setTemperatureValue(aiSettings.aiTemperature ?? 0.7);
      setTemperatureInitialized(true);
    }
  }, [aiSettings, temperatureInitialized]);

  const handleTestEmailChange = async (email: string) => {
    setTestEmail(email);
    setTestEmailError(null);
    
    if (email && email.includes("@")) {
      try {
        const result = await validateEmailMutation.mutateAsync(email);
        if (!result.valid) {
          setTestEmailError(result.reason || "Email not allowed");
        }
      } catch {
        // Validation endpoint error - don't block user
      }
    }
  };

  const createZoneMutation = useMutation({
    mutationFn: async (data: ZoneFormData) => {
      return await apiRequest("POST", "/api/zones", data);
    },
    onSuccess: () => {
      toast({
        title: "Kennel created successfully",
        description: "The new kennel has been added.",
      });
      setIsZoneDrawerOpen(false);
      zoneForm.reset();
      queryClient.invalidateQueries({ queryKey: ["/api/zones"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create kennel",
        variant: "destructive",
      });
    },
  });

  const updateZoneMutation = useMutation({
    mutationFn: async ({ zoneId, data }: { zoneId: string; data: Partial<ZoneFormData> }) => {
      return await apiRequest("PATCH", `/api/zones/${zoneId}`, data);
    },
    onSuccess: () => {
      toast({
        title: "Kennel updated successfully",
        description: "The kennel has been updated.",
      });
      zoneForm.reset();
      setEditingZone(null);
      setIsZoneDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/zones"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update kennel",
        variant: "destructive",
      });
    },
  });

  const deleteZoneMutation = useMutation({
    mutationFn: async (zoneId: string) => {
      return await apiRequest("DELETE", `/api/zones/${zoneId}`);
    },
    onSuccess: () => {
      toast({
        title: "Kennel deleted successfully",
        description: "The kennel has been removed.",
      });
      setDeletingZone(null);
      queryClient.invalidateQueries({ queryKey: ["/api/zones"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Cannot delete kennel",
        description: error.message || "Failed to delete kennel",
        variant: "destructive",
      });
      setDeletingZone(null);
    },
  });

  const createAliasMutation = useMutation({
    mutationFn: async (data: AliasFormData) => {
      return await apiRequest("POST", `/api/zones/${data.zoneId}/aliases`, {
        aliasCode: data.aliasCode,
      });
    },
    onSuccess: () => {
      toast({
        title: "Alias added successfully",
        description: "The alias has been created.",
      });
      setIsAliasDialogOpen(false);
      aliasForm.reset();
      queryClient.invalidateQueries({ queryKey: ["/api/zones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/zones/all-aliases"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add alias",
        variant: "destructive",
      });
    },
  });

  const deleteAliasMutation = useMutation({
    mutationFn: async (aliasId: string) => {
      return await apiRequest("DELETE", `/api/zones/aliases/${aliasId}`);
    },
    onSuccess: () => {
      toast({
        title: "Alias removed successfully",
        description: "The alias has been deleted.",
      });
      setDeletingAlias(null);
      queryClient.invalidateQueries({ queryKey: ["/api/zones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/zones/all-aliases"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove alias",
        variant: "destructive",
      });
      setDeletingAlias(null);
    },
  });

  const createLocationMutation = useMutation({
    mutationFn: async (data: LocationFormData) => {
      return await apiRequest("POST", "/api/locations", data);
    },
    onSuccess: () => {
      toast({
        title: "Location created successfully",
        description: "The new location has been added.",
      });
      setIsLocationDialogOpen(false);
      locationForm.reset();
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create location",
        variant: "destructive",
      });
    },
  });

  const updateLocationMutation = useMutation({
    mutationFn: async ({ locationId, data }: { locationId: string; data: Partial<LocationFormData> }) => {
      return await apiRequest("PATCH", `/api/locations/${locationId}`, data);
    },
    onSuccess: () => {
      toast({
        title: "Location updated successfully",
        description: "The location has been updated.",
      });
      locationForm.reset();
      setEditingLocation(null);
      setIsLocationDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update location",
        variant: "destructive",
      });
    },
  });

  const deactivateLocationMutation = useMutation({
    mutationFn: async (locationId: string) => {
      return await apiRequest("PATCH", `/api/locations/${locationId}/deactivate`);
    },
    onSuccess: () => {
      toast({
        title: "Location deactivated",
        description: "The location has been deactivated.",
      });
      setDeactivatingLocation(null);
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Cannot deactivate location",
        description: error.message || "Failed to deactivate location",
        variant: "destructive",
      });
      setDeactivatingLocation(null);
    },
  });

  const activateLocationMutation = useMutation({
    mutationFn: async (locationId: string) => {
      return await apiRequest("PATCH", `/api/locations/${locationId}/activate`);
    },
    onSuccess: () => {
      toast({
        title: "Location activated",
        description: "The location has been activated.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to activate location",
        variant: "destructive",
      });
    },
  });

  const openAddZoneDrawer = () => {
    setEditingZone(null);
    zoneForm.reset({
      zoneCode: "",
      areaLabel: "",
      location: undefined as unknown as Location,
      runs: 0,
      stainless: 0,
      variety: 0,
      other: 0,
    });
    setIsZoneDrawerOpen(true);
  };

  const openEditZoneDrawer = (zone: Zone) => {
    setEditingZone(zone);
    zoneForm.reset({
      zoneCode: zone.zoneCode,
      areaLabel: zone.areaLabel,
      location: zone.location,
      runs: zone.runs,
      stainless: zone.stainless,
      variety: zone.variety,
      other: zone.other,
    });
    setIsZoneDrawerOpen(true);
  };

  const onSubmitZone = (data: ZoneFormData) => {
    if (editingZone) {
      const { zoneCode, ...updateData } = data;
      updateZoneMutation.mutate({
        zoneId: editingZone.id,
        data: updateData,
      });
    } else {
      createZoneMutation.mutate(data);
    }
  };

  const onSubmitAlias = (data: AliasFormData) => {
    createAliasMutation.mutate(data);
  };

  const openAddLocationDialog = () => {
    setEditingLocation(null);
    locationForm.reset({
      code: "",
      label: "",
    });
    setIsLocationDialogOpen(true);
  };

  const openEditLocationDialog = (location: ShelterLocation) => {
    setEditingLocation(location);
    locationForm.reset({
      code: location.code,
      label: location.label,
    });
    setIsLocationDialogOpen(true);
  };

  const onSubmitLocation = (data: LocationFormData) => {
    if (editingLocation) {
      updateLocationMutation.mutate({
        locationId: editingLocation.id,
        data: { label: data.label },
      });
    } else {
      createLocationMutation.mutate(data);
    }
  };

  const isLoading = isAuthLoading || isZonesLoading;

  if (isLoading) {
    return (
      <div className="flex-1 px-4 sm:px-6 pb-6 pt-4" data-testid="page-settings">
        <PageHeader
          breadcrumb={[{ label: "Administration" }, { label: "Settings" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-settings">
              <SettingsIcon className="w-6 h-6 text-primary" />
              Settings
            </span>
          }
        />

        <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
          <TabsList>
            <TabsTrigger value="zones" data-testid="tab-zones">
              Kennels
            </TabsTrigger>
          </TabsList>

          <TabsContent value="zones" className="space-y-6 mt-6">
            <Card data-testid="card-zones">
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <div>
                  <CardTitle>Kennels</CardTitle>
                  <CardDescription>
                    Manage kennels and their capacity limits.
                  </CardDescription>
                </div>
                <Button disabled data-testid="button-add-zone">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Kennel
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table className="table-zebra" data-testid="table-zones">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kennel Code</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Area Label</TableHead>
                        <TableHead className="text-right">Runs</TableHead>
                        <TableHead className="text-right">Stainless</TableHead>
                        <TableHead className="text-right">Variety</TableHead>
                        <TableHead className="text-right">Other</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[1, 2, 3].map((i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                          <TableCell className="text-right"><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                          <TableCell className="text-right"><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                          <TableCell className="text-right"><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                          <TableCell className="text-right"><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                          <TableCell className="text-right"><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Skeleton className="h-8 w-8" />
                              <Skeleton className="h-8 w-8" />
                            </div>
                          </TableCell>
                      </TableRow>
                    ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  if (!currentUser?.isAdmin) {
    return (
      <div
        className="flex-1 flex items-center justify-center px-4 sm:px-6 pb-6 pt-4"
        data-testid="settings-access-denied"
      >
        <Card className="max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center">
              <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2" data-testid="text-access-denied-title">
                Access Denied
              </h2>
              <p className="text-muted-foreground mb-6" data-testid="text-access-denied-message">
                You don't have permission to access this page.
              </p>
              <Button asChild data-testid="button-go-to-dashboard">
                <a href="/dashboard">Go to Dashboard</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!currentUser?.isSuperAdmin && superAdminOnlyTabs.includes(activeTab)) {
    return (
      <div className="flex-1 px-4 sm:px-6 pb-6 pt-4" data-testid="page-settings">
        <PageHeader
          breadcrumb={[{ label: "Administration" }, { label: "Settings" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-settings">
              <SettingsIcon className="w-6 h-6 text-primary" />
              Settings
            </span>
          }
        />
        <div className="flex items-center justify-center mt-12">
          <Card className="max-w-md w-full">
            <CardContent className="p-8">
              <div className="text-center">
                <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-semibold mb-2" data-testid="text-settings-not-authorized-title">
                  Not Authorized
                </h2>
                <p className="text-muted-foreground mb-6" data-testid="text-settings-not-authorized-message">
                  This section is only available to authorized personnel. If you need access, please contact your system administrator.
                </p>
                <Button onClick={() => handleTabChange("zones")} data-testid="button-go-to-settings">
                  Go to Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isZonesError) {
    const errorMessage = zonesError instanceof Error ? zonesError.message : "Unknown error";
    return (
      <div className="flex-1 px-4 sm:px-6 pb-6 pt-4" data-testid="page-settings">
        <PageHeader
          breadcrumb={[{ label: "Administration" }, { label: "Settings" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-settings">
              <SettingsIcon className="w-6 h-6 text-primary" />
              Settings
            </span>
          }
        />

        <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
          <TabsList>
            <TabsTrigger value="zones" data-testid="tab-zones">
              Kennels
            </TabsTrigger>
          </TabsList>

          <TabsContent value="zones" className="space-y-6 mt-6">
            <Card data-testid="card-zones">
              <CardHeader>
                <CardTitle>Kennels</CardTitle>
                <CardDescription>
                  Manage kennels and their capacity limits.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <p className="text-destructive mb-4">
                    Failed to load kennels: {errorMessage}
                  </p>
                  <Button onClick={() => refetchZones()} data-testid="button-retry-zones">
                    <RotateCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="flex-1 px-4 sm:px-6 pb-6 pt-4" data-testid="page-settings">
      <PageHeader
        breadcrumb={[{ label: "Administration" }, { label: "Settings" }]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-settings">
            <SettingsIcon className="w-6 h-6 text-primary" />
            Settings
          </span>
        }
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-max sm:w-auto">
            <TabsTrigger value="zones" data-testid="tab-zones">
              <Home className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Kennels</span>
            </TabsTrigger>
            <TabsTrigger value="locations" data-testid="tab-locations">
              <MapPin className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Locations</span>
            </TabsTrigger>
            <TabsTrigger value="branding" data-testid="tab-branding">
              <Palette className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Branding</span>
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">
              <Mail className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            {currentUser?.isSuperAdmin && (
              <TabsTrigger value="email-ingest" data-testid="tab-email-ingest">
                <Inbox className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Ingest</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="system" data-testid="tab-system">
              <Server className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">System</span>
            </TabsTrigger>
            {currentUser?.isSuperAdmin && (
              <TabsTrigger value="ai" data-testid="tab-ai">
                <Sparkles className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">AI</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="behavior" data-testid="tab-behavior">
              <Activity className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Behavior</span>
            </TabsTrigger>
            {currentUser?.isAdmin && (
              <TabsTrigger value="animal-profiles" data-testid="tab-animal-profiles">
                <ClipboardList className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Animal Profiles</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="snapshots" data-testid="tab-snapshots">
              <Clock className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Snapshots</span>
            </TabsTrigger>
            {currentUser?.isSuperAdmin && (
              <TabsTrigger value="hub-shadow" data-testid="tab-hub-shadow">
                <ShieldAlert className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Hub Shadow</span>
              </TabsTrigger>
            )}
            {currentUser?.isSuperAdmin && (
              <TabsTrigger value="security" data-testid="tab-security">
                <Shield className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Security</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="breeds" data-testid="tab-breeds">
              <PawPrint className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Breeds</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="zones" className="space-y-6 mt-6">
          <Card data-testid="card-zones">
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle>Kennels</CardTitle>
                <CardDescription>
                  Manage kennels and their capacity limits.
                </CardDescription>
              </div>
              <Button onClick={openAddZoneDrawer} data-testid="button-add-zone">
                <Plus className="h-4 w-4 mr-2" />
                Add Kennel
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="table-zebra" data-testid="table-zones">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kennel Code</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Area Label</TableHead>
                      <TableHead className="text-right">Runs</TableHead>
                      <TableHead className="text-right">Stainless</TableHead>
                      <TableHead className="text-right">Variety</TableHead>
                      <TableHead className="text-right">Other</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {zones.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          No kennels configured. Click "Add Kennel" to create one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      zones.map((zone) => (
                        <TableRow key={zone.id} data-testid={`row-zone-${zone.id}`}>
                          <TableCell className="font-medium">{zone.zoneCode}</TableCell>
                          <TableCell data-testid={`text-zone-location-${zone.id}`}>{zone.locationLabel || LOCATION_LABELS[zone.location as keyof typeof LOCATION_LABELS] || zone.location}</TableCell>
                          <TableCell>{zone.areaLabel}</TableCell>
                          <TableCell className="text-right">{zone.runs}</TableCell>
                          <TableCell className="text-right">{zone.stainless}</TableCell>
                          <TableCell className="text-right">{zone.variety}</TableCell>
                          <TableCell className="text-right">{zone.other}</TableCell>
                          <TableCell className="text-right font-medium">{zone.totalCapacity}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditZoneDrawer(zone)}
                                data-testid={`button-edit-zone-${zone.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeletingZone(zone)}
                                data-testid={`button-delete-zone-${zone.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-aliases">
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle>Aliases</CardTitle>
                <CardDescription>
                  Map alternate kennel codes to canonical kennels.
                </CardDescription>
              </div>
              <Button onClick={() => setIsAliasDialogOpen(true)} data-testid="button-add-alias">
                <Plus className="h-4 w-4 mr-2" />
                Add Alias
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="table-zebra" data-testid="table-aliases">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Alias Code</TableHead>
                      <TableHead>Maps To</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isAliasesLoading ? (
                      [1, 2].map((i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                        </TableRow>
                      ))
                    ) : aliases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No aliases configured. Click "Add Alias" to create one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      aliases.map((alias) => (
                        <TableRow key={alias.id} data-testid={`row-alias-${alias.id}`}>
                          <TableCell className="font-medium">{alias.aliasCode}</TableCell>
                          <TableCell>{alias.zoneName}</TableCell>
                          <TableCell>{formatDate(alias.createdAt)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeletingAlias(alias)}
                              data-testid={`button-delete-alias-${alias.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="locations" className="space-y-6 mt-6">
          <Card data-testid="card-locations">
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Shelter Locations
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                  Manage shelter locations that kennels can be assigned to.
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="inline-flex" data-testid="tooltip-locations-deactivation-info">
                        <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p>Locations that are currently referenced by one or more kennels cannot be deactivated. Update or reassign their kennels first.</p>
                    </TooltipContent>
                  </Tooltip>
                </CardDescription>
              </div>
              {currentUser?.isSuperAdmin && (
                <Button onClick={openAddLocationDialog} data-testid="button-add-location">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Location
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                {isLocationsLoading ? (
                  <Table className="table-zebra" data-testid="table-locations">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead className="text-center">Kennels</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[1, 2, 3, 4].map((i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                          <TableCell className="text-center"><Skeleton className="h-4 w-8 mx-auto" /></TableCell>
                          <TableCell className="text-center"><Skeleton className="h-6 w-16 mx-auto" /></TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Skeleton className="h-8 w-8" />
                              <Skeleton className="h-8 w-8" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : isLocationsError ? (
                  <div className="text-center py-8">
                    <p className="text-destructive mb-4">
                      Failed to load locations: {locationsError instanceof Error ? locationsError.message : "Unknown error"}
                    </p>
                    <Button onClick={() => refetchLocations()} data-testid="button-retry-locations">
                      <RotateCw className="h-4 w-4 mr-2" />
                      Retry
                    </Button>
                  </div>
                ) : (
                  <Table className="table-zebra" data-testid="table-locations">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead className="text-center">Kennels</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {locationsList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            {currentUser?.isSuperAdmin 
                              ? 'No locations configured. Click "Add Location" to create one.'
                              : "No locations configured. Contact your system administrator to add locations."}
                          </TableCell>
                        </TableRow>
                      ) : (
                        locationsList.map((location) => (
                          <TableRow key={location.id} data-testid={`row-location-${location.id}`}>
                            <TableCell className="font-medium font-mono">{location.code}</TableCell>
                            <TableCell>{location.label}</TableCell>
                            <TableCell className="text-center">{location.zoneCount}</TableCell>
                            <TableCell className="text-center">
                              {location.isActive ? (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                  <Check className="h-3 w-3" />
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                  <X className="h-3 w-3" />
                                  Inactive
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditLocationDialog(location)}
                                  data-testid={`button-edit-location-${location.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                {currentUser?.isSuperAdmin && (
                                  location.isActive ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setDeactivatingLocation(location)}
                                      disabled={location.zoneCount > 0}
                                      title={location.zoneCount > 0 ? "Cannot deactivate: kennels are using this location" : "Deactivate location"}
                                      data-testid={`button-deactivate-location-${location.id}`}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => activateLocationMutation.mutate(location.id)}
                                      data-testid={`button-activate-location-${location.id}`}
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                  )
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding & Metadata Tab (Gate I14) */}
        <TabsContent value="branding" className="space-y-6 mt-6">
          <Card data-testid="card-branding-identity">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                App Identity
              </CardTitle>
              <CardDescription>
                Customize your application name, title, and logo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isAppSettingsLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-24 w-24" />
                </div>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="branding-app-name">App Name</Label>
                      <Input
                        id="branding-app-name"
                        data-testid="input-app-name"
                        value={brandingAppName}
                        onChange={(e) => setBrandingAppName(e.target.value)}
                        placeholder="BETI"
                        maxLength={100}
                      />
                      <p className="text-xs text-muted-foreground">
                        Displayed in the sidebar and throughout the app.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="branding-tab-title">Browser Tab Title</Label>
                      <Input
                        id="branding-tab-title"
                        data-testid="input-tab-title"
                        value={brandingTabTitle}
                        onChange={(e) => setBrandingTabTitle(e.target.value)}
                        placeholder="BETI | Behavior Evals, Tracking & Insights"
                        maxLength={200}
                      />
                      <p className="text-xs text-muted-foreground">
                        Shown in the browser tab.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Label>Logo</Label>
                    <div className="flex items-center gap-6">
                      <div className="h-24 w-24 rounded-full bg-primary overflow-hidden flex items-end justify-center flex-shrink-0">
                        <img
                          src={brandingLogoPreview || `${appSettingsData?.logoUrl || "/api/assets/branding/logo"}?t=${logoTimestamp}`}
                          alt="Current logo"
                          className="w-[95%] object-contain translate-y-[12%]"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <label>
                            <input
                              type="file"
                              accept="image/png,image/jpeg"
                              onChange={handleLogoFileChange}
                              className="hidden"
                              data-testid="input-logo-file"
                            />
                            <Button variant="outline" asChild>
                              <span>
                                <Upload className="h-4 w-4 mr-2" />
                                Choose File
                              </span>
                            </Button>
                          </label>
                          {brandingLogoFile && (
                            <Button
                              onClick={handleUploadLogo}
                              disabled={uploadLogoMutation.isPending}
                              data-testid="button-upload-logo"
                            >
                              {uploadLogoMutation.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4 mr-2" />
                              )}
                              Upload
                            </Button>
                          )}
                          {appSettingsData?.logoProvider === "s3" && (
                            <Button
                              variant="outline"
                              onClick={handleResetLogo}
                              disabled={resetLogoMutation.isPending}
                              data-testid="button-reset-logo"
                            >
                              {resetLogoMutation.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <RotateCw className="h-4 w-4 mr-2" />
                              )}
                              Reset to Default
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          PNG or JPEG, max 2MB. Logo appears in the sidebar.
                        </p>
                        {brandingLogoFile && (
                          <p className="text-xs text-muted-foreground">
                            Selected: {brandingLogoFile.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card data-testid="card-branding-seo">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                SEO & Social Sharing
              </CardTitle>
              <CardDescription>
                Optional metadata for search engines and social media previews.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isAppSettingsLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="branding-meta-description">Meta Description</Label>
                    <Textarea
                      id="branding-meta-description"
                      data-testid="input-meta-description"
                      value={brandingMetaDescription}
                      onChange={(e) => setBrandingMetaDescription(e.target.value)}
                      placeholder="A brief description of your application for search engines..."
                      maxLength={300}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      {brandingMetaDescription.length}/300 characters
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="branding-og-title">Open Graph Title</Label>
                      <Input
                        id="branding-og-title"
                        data-testid="input-og-title"
                        value={brandingOgTitle}
                        onChange={(e) => setBrandingOgTitle(e.target.value)}
                        placeholder="Title for social media shares"
                        maxLength={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="branding-og-description">Open Graph Description</Label>
                      <Input
                        id="branding-og-description"
                        data-testid="input-og-description"
                        value={brandingOgDescription}
                        onChange={(e) => setBrandingOgDescription(e.target.value)}
                        placeholder="Description for social media shares"
                        maxLength={300}
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card data-testid="card-branding-robots">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Search Engine Visibility
              </CardTitle>
              <CardDescription>
                Control whether search engines can index this application.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isAppSettingsLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="branding-robots-noindex">Block search engines</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable noindex/nofollow directives. Recommended for internal applications.
                    </p>
                  </div>
                  <Switch
                    id="branding-robots-noindex"
                    data-testid="switch-robots-noindex"
                    checked={brandingRobotsNoindex}
                    onCheckedChange={setBrandingRobotsNoindex}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="card-branding-theme-colors">
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    Theme Colors
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Override the default page background and card surface colors for light and dark mode. Changes apply immediately across all users.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="button-reset-theme-colors"
                  onClick={handleResetThemeColors}
                  disabled={updateAppSettingsMutation.isPending || (!appSettingsData?.themeBgLight && !appSettingsData?.themeCardLight && !appSettingsData?.themeBgDark && !appSettingsData?.themeCardDark)}
                >
                  <RotateCw className="h-4 w-4 mr-2" />
                  Reset to Defaults
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isAppSettingsLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-4">
                    <p className="text-sm font-medium">Light Mode</p>
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="theme-bg-light">Page Background</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            id="theme-bg-light"
                            data-testid="input-theme-bg-light"
                            className="h-9 w-16 cursor-pointer rounded-md border border-input bg-transparent p-1"
                            value={isValidHsl(themeBgLight) ? hslToHex(themeBgLight) : "#f0f2f5"}
                            onChange={(e) => setThemeBgLight(hexToHsl(e.target.value))}
                          />
                          <Input
                            placeholder="e.g. 220 20% 94%"
                            value={themeBgLight}
                            onChange={(e) => setThemeBgLight(e.target.value)}
                            className="font-mono text-sm"
                            data-testid="input-theme-bg-light-hsl"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">HSL format: H S% L%</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="theme-card-light">Card / Component Background</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            id="theme-card-light"
                            data-testid="input-theme-card-light"
                            className="h-9 w-16 cursor-pointer rounded-md border border-input bg-transparent p-1"
                            value={isValidHsl(themeCardLight) ? hslToHex(themeCardLight) : "#ffffff"}
                            onChange={(e) => setThemeCardLight(hexToHsl(e.target.value))}
                          />
                          <Input
                            placeholder="e.g. 0 0% 100%"
                            value={themeCardLight}
                            onChange={(e) => setThemeCardLight(e.target.value)}
                            className="font-mono text-sm"
                            data-testid="input-theme-card-light-hsl"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">HSL format: H S% L%</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <p className="text-sm font-medium">Dark Mode</p>
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="theme-bg-dark">Page Background</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            id="theme-bg-dark"
                            data-testid="input-theme-bg-dark"
                            className="h-9 w-16 cursor-pointer rounded-md border border-input bg-transparent p-1"
                            value={isValidHsl(themeBgDark) ? hslToHex(themeBgDark) : "#090d16"}
                            onChange={(e) => setThemeBgDark(hexToHsl(e.target.value))}
                          />
                          <Input
                            placeholder="e.g. 222 47% 5%"
                            value={themeBgDark}
                            onChange={(e) => setThemeBgDark(e.target.value)}
                            className="font-mono text-sm"
                            data-testid="input-theme-bg-dark-hsl"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">HSL format: H S% L%</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="theme-card-dark">Card / Component Background</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            id="theme-card-dark"
                            data-testid="input-theme-card-dark"
                            className="h-9 w-16 cursor-pointer rounded-md border border-input bg-transparent p-1"
                            value={isValidHsl(themeCardDark) ? hslToHex(themeCardDark) : "#121929"}
                            onChange={(e) => setThemeCardDark(hexToHsl(e.target.value))}
                          />
                          <Input
                            placeholder="e.g. 222 47% 11%"
                            value={themeCardDark}
                            onChange={(e) => setThemeCardDark(e.target.value)}
                            className="font-mono text-sm"
                            data-testid="input-theme-card-dark-hsl"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">HSL format: H S% L%</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              onClick={handleSaveBrandingSettings}
              disabled={updateAppSettingsMutation.isPending || !brandingAppName || !brandingTabTitle}
              data-testid="button-save-branding"
            >
              {updateAppSettingsMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email" className="space-y-6 mt-6">
          <Card data-testid="card-email-config">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Configuration
              </CardTitle>
              <CardDescription>
                Mailgun configuration status and test actions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isEmailSettingsLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-64" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ) : emailSettings ? (
                <div className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Status</p>
                      <p className="flex items-center gap-2" data-testid="text-email-status">
                        {emailSettings.configured ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="font-medium text-green-600 dark:text-green-400">Configured</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="h-4 w-4 text-yellow-500" />
                            <span className="font-medium text-yellow-600 dark:text-yellow-400">Not Configured</span>
                          </>
                        )}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Environment</p>
                      <p className="font-medium" data-testid="text-email-environment">
                        {emailSettings.isProduction ? "Production" : "Development / Test"}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4 bg-muted/50">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground" data-testid="text-email-safety-note">
                        {emailSettings.safetyNote}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Failed to load email settings</p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="card-test-email">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Test Email Actions
              </CardTitle>
              <CardDescription>
                Send test emails to verify email delivery. Emails must pass F9 safety rules.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="test-email">Test Email Address</Label>
                <Input
                  id="test-email"
                  type="email"
                  placeholder="chris+test@ccwc.co"
                  value={testEmail}
                  onChange={(e) => handleTestEmailChange(e.target.value)}
                  data-testid="input-test-email"
                />
                {testEmailError && (
                  <p className="text-sm text-destructive" data-testid="text-email-error">
                    {testEmailError}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  In non-production, only chris+*@ccwc.co addresses are allowed.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => testEmail && testInviteEmailMutation.mutate(testEmail)}
                  disabled={!testEmail || !!testEmailError || testInviteEmailMutation.isPending}
                  data-testid="button-test-invite"
                >
                  {testInviteEmailMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  <Mail className="h-4 w-4 mr-2" />
                  Send Test Invite
                </Button>
                <Button
                  onClick={() => testEmail && testResetEmailMutation.mutate(testEmail)}
                  disabled={!testEmail || !!testEmailError || testResetEmailMutation.isPending}
                  data-testid="button-test-reset"
                >
                  {testResetEmailMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  <Lock className="h-4 w-4 mr-2" />
                  Send Test Password Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Info Tab */}
        <TabsContent value="system" className="space-y-6 mt-6">
          <Card data-testid="card-system-info">
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  System Information
                </CardTitle>
                <CardDescription>
                  Application version, environment, and health status.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetchSystemInfo()}
                data-testid="button-refresh-system"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {isSystemInfoLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ) : systemInfo ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Version</p>
                    <p className="font-mono text-sm" data-testid="text-version">
                      {systemInfo.version}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Build Hash</p>
                    <p className="font-mono text-sm truncate" data-testid="text-build-hash">
                      {systemInfo.buildHash}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Environment</p>
                    <p className="font-medium capitalize" data-testid="text-environment">
                      {systemInfo.environment}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Security Mode</p>
                    <p className="font-medium" data-testid="text-security-mode">
                      {systemInfo.securityMode}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">API Status</p>
                    <p className="flex items-center gap-2" data-testid="text-api-status">
                      {systemInfo.apiReachable ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-green-600 dark:text-green-400">Reachable</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className="text-red-600 dark:text-red-400">Unreachable</span>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Database</p>
                    <p className="flex items-center gap-2" data-testid="text-db-status">
                      {systemInfo.database.healthy ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-green-600 dark:text-green-400">Healthy</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className="text-red-600 dark:text-red-400">Error</span>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="space-y-1 sm:col-span-2 lg:col-span-3">
                    <p className="text-sm text-muted-foreground">Last Check</p>
                    <p className="text-sm" data-testid="text-timestamp">
                      {format(new Date(systemInfo.timestamp), "MMM d, yyyy 'at' h:mm:ss a")}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Failed to load system information</p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="card-rate-limits">
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Rate Limit Lockouts
                </CardTitle>
                <CardDescription>
                  Active rate limit lockouts (read-only). Auto-refreshes every 30 seconds.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetchRateLimits()}
                data-testid="button-refresh-lockouts"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {isRateLimitsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : rateLimits ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">Security Mode:</span>
                    <span className="font-medium" data-testid="text-rate-limit-mode">
                      {rateLimits.securityMode}
                    </span>
                    <span className="text-muted-foreground">|</span>
                    <span className="text-muted-foreground">Active Lockouts:</span>
                    <span className="font-medium" data-testid="text-lockout-count">
                      {rateLimits.lockedCount}
                    </span>
                  </div>

                  {rateLimits.lockedCount === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                      <p>No active lockouts</p>
                    </div>
                  ) : (
                    <Table className="table-zebra" data-testid="table-lockouts">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Identifier</TableHead>
                          <TableHead>Locked Until</TableHead>
                          <TableHead className="text-right">Remaining</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rateLimits.lockedEntries.map((entry, index) => (
                          <TableRow key={`${entry.type}-${entry.key}-${index}`} data-testid={`row-lockout-${index}`}>
                            <TableCell className="font-mono text-sm">{entry.type}</TableCell>
                            <TableCell className="font-mono text-sm truncate max-w-[200px]">
                              {entry.key}
                            </TableCell>
                            <TableCell className="text-sm">
                              {format(new Date(entry.lockedUntil), "MMM d, h:mm:ss a")}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                <Clock className="h-3 w-3" />
                                {entry.remainingSeconds}s
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">Failed to load rate limit data</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Tab */}
        <TabsContent value="ai" className="space-y-6 mt-6">
          <Card data-testid="card-ai-config">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                AI Configuration
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="inline-flex" data-testid="tooltip-ai-security-info">
                      <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-sm text-xs leading-relaxed">
                    <p className="font-semibold mb-1">Data security</p>
                    <p className="font-normal">Your API key is encrypted using AES-256 before being stored and is never accessible in plain text after saving.</p>
                    <p className="font-normal mt-1">When an AI summary is requested, only that animal's behavioral observations are sent to OpenAI over a secure HTTPS connection. OpenAI does not use API requests to train their models. No owner, adopter, or contact information is ever included in a request.</p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <CardDescription>
                Configure AI-generated behavior summaries for animal profiles.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isAiSettingsLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-64" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ) : isAiSettingsError ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                  <p className="text-muted-foreground mb-4">Failed to load AI settings</p>
                  <Button onClick={() => refetchAiSettings()} variant="outline" data-testid="button-retry-ai">
                    <RotateCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </div>
              ) : aiSettings ? (
                <div className="space-y-6">
                  {/* Enable/Disable Toggle */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="ai-toggle" className="text-sm font-medium">Enable AI Summaries</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="inline-flex" data-testid="tooltip-ai-toggle-info">
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed space-y-1.5">
                          <p className="font-semibold">Who sees AI summaries</p>
                          <p className="font-normal">When enabled, AI-generated summaries appear on animal profile pages for all logged-in staff. When disabled, no summaries are generated or displayed to anyone.</p>
                          <p className="font-normal">An OpenAI API key must be configured for summaries to generate regardless of this setting.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${aiSettings.aiSummariesEnabled ? "text-green-600 dark:text-green-400 font-medium" : "text-muted-foreground"}`} data-testid="text-ai-enabled-status">
                        {aiSettings.aiSummariesEnabled ? "Enabled" : "Disabled"}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateAiSettingsMutation.mutate(!aiSettings.aiSummariesEnabled)}
                        disabled={updateAiSettingsMutation.isPending}
                        data-testid="button-toggle-ai"
                      >
                        {updateAiSettingsMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {aiSettings.aiSummariesEnabled ? "Disable" : "Enable"}
                      </Button>
                    </div>
                  </div>

                  {/* OpenAI Key Input */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="openai-key" className="text-sm font-medium">OpenAI API Key</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="inline-flex" data-testid="tooltip-openai-key-info">
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed">
                          <p>Your key is encrypted before being stored and is never visible in plain text after saving. To update, enter a new key and save. To remove it, leave the field empty and click Clear.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-center gap-2" data-testid="text-openai-key-status">
                      {aiSettings.openaiApiKeyConfigured ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="font-medium text-green-600 dark:text-green-400">Configured</span>
                          <span className="text-sm text-muted-foreground">
                            ({aiSettings.openaiApiKeyType === "project" ? "Project key" : "API key"})
                          </span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          <span className="font-medium text-yellow-600 dark:text-yellow-400">Not Configured</span>
                        </>
                      )}
                    </div>
                    {aiSettings.encryptionConfigured === false && (
                      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-3">
                        <p className="text-sm text-red-700 dark:text-red-400">
                          SETTINGS_ENCRYPTION_KEY not configured. Cannot securely store API keys.
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        id="openai-key"
                        type="password"
                        placeholder="sk-..."
                        value={openaiKeyInput}
                        onChange={(e) => setOpenaiKeyInput(e.target.value)}
                        disabled={updateOpenaiKeyMutation.isPending || aiSettings.encryptionConfigured === false}
                        data-testid="input-openai-key"
                        className="font-mono"
                      />
                      <Button
                        onClick={() => updateOpenaiKeyMutation.mutate(openaiKeyInput)}
                        disabled={updateOpenaiKeyMutation.isPending || (!openaiKeyInput && !aiSettings.openaiApiKeyConfigured) || aiSettings.encryptionConfigured === false}
                        data-testid="button-save-openai-key"
                      >
                        {updateOpenaiKeyMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {openaiKeyInput ? "Save" : "Clear"}
                      </Button>
                    </div>
                  </div>

                  {/* Model Selection */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium">OpenAI Model</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="inline-flex" data-testid="tooltip-openai-model-info">
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed space-y-1.5">
                          <p className="font-medium">Choosing a model</p>
                          <p><span className="font-medium">gpt-4o-mini / gpt-4.1-mini</span> — Faster and lower cost. A good default for routine summaries where the behavioral data is clear and consistent.</p>
                          <p><span className="font-medium">gpt-4o / gpt-4.1</span> — Higher quality output, better at nuance and complex or sparse behavioral patterns. Slower and more expensive per summary.</p>
                          <p>The 4.1 models are newer with improved instruction-following. The 4o models are well-established and widely tested.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Select
                      value={aiSettings.openaiModel}
                      onValueChange={(value) => updateModelMutation.mutate(value)}
                      disabled={updateModelMutation.isPending}
                    >
                      <SelectTrigger className="w-48" data-testid="select-openai-model">
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {aiSettings.allowedModels?.map((model) => (
                          <SelectItem key={model} value={model} data-testid={`option-model-${model}`}>
                            {model}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Temperature */}
                  <div className="space-y-3" data-testid="section-ai-temperature">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium">Temperature</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="inline-flex" data-testid="tooltip-temperature-info">
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed space-y-1.5">
                          <p className="font-semibold">Controls output variation</p>
                          <p className="font-normal">Lower values (0.0–0.4) produce more consistent, predictable summaries. Higher values (0.6–1.0) produce more varied prose that may feel more natural but less uniform across animals.</p>
                          <p className="font-normal">The default of 0.7 is a balanced starting point for most use cases.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-center gap-4">
                      <Slider
                        min={0}
                        max={1}
                        step={0.1}
                        value={[temperatureValue]}
                        onValueChange={([val]) => setTemperatureValue(val)}
                        onValueCommit={([val]) => updateTemperatureMutation.mutate(val)}
                        disabled={updateTemperatureMutation.isPending}
                        className="w-48"
                        data-testid="slider-temperature"
                      />
                      <span className="text-sm tabular-nums text-muted-foreground w-6" data-testid="text-temperature-value">
                        {temperatureValue.toFixed(1)}
                      </span>
                      {updateTemperatureMutation.isPending && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground w-48">
                      <span>Consistent</span>
                      <span>Varied</span>
                    </div>
                  </div>

                  {/* AI Prompt Guidance */}
                  <div className="space-y-4 pt-4 border-t" data-testid="section-prompt-guidance">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Label className="text-base font-medium">AI Prompt Guidance</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" className="inline-flex" data-testid="tooltip-prompt-guidance-info">
                              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed space-y-1.5">
                            <p>These instructions influence wording and tone only. They cannot add recommendations or override safety rules.</p>
                            <p>Admins can also provide additional instructions in the prompt builder for individual prompt types, which are combined with these settings. Use these fields to set organization-wide defaults, and the prompt builder for more targeted tuning.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    <div className="rounded-lg border p-4 bg-muted/50">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                        <p className="text-sm text-muted-foreground" data-testid="text-ai-warning">
                          AI-generated summaries are non-authoritative, display-only, and do not include recommendations.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="system-prompt" className="text-sm font-medium">System Instructions</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="inline-flex" data-testid="tooltip-system-prompt-info">
                                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed">
                              <p>Sets the operating context and constraints for the AI — what it should and shouldn't include, how to handle sensitive topics, and any organizational rules. This shapes the substance of what gets written.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0" data-testid="text-system-prompt-count">
                          {systemPromptInput.length} / 2000
                        </span>
                      </div>
                      <Textarea
                        id="system-prompt"
                        placeholder="Define terminology, framing, or organizational context..."
                        value={systemPromptInput}
                        onChange={(e) => setSystemPromptInput(e.target.value)}
                        maxLength={2000}
                        rows={4}
                        data-testid="textarea-system-prompt"
                        className="resize-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="style-prompt" className="text-sm font-medium">Style Guidance</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="inline-flex" data-testid="tooltip-style-prompt-info">
                                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed">
                              <p>Controls how the summary reads — tone, length, vocabulary, and formatting. Use this to match your organization's communication style, e.g. clinical vs. conversational, brief vs. detailed.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0" data-testid="text-style-prompt-count">
                          {stylePromptInput.length} / 2000
                        </span>
                      </div>
                      <Textarea
                        id="style-prompt"
                        placeholder="Define tone, formatting preferences, or style conventions..."
                        value={stylePromptInput}
                        onChange={(e) => setStylePromptInput(e.target.value)}
                        maxLength={2000}
                        rows={4}
                        data-testid="textarea-style-prompt"
                        className="resize-none"
                      />
                    </div>

                    <Button
                      onClick={() => updatePromptsMutation.mutate({
                        systemPrompt: systemPromptInput || null,
                        stylePrompt: stylePromptInput || null,
                      })}
                      disabled={updatePromptsMutation.isPending}
                      data-testid="button-save-prompts"
                    >
                      {updatePromptsMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Save Prompts
                    </Button>
                  </div>

                </div>
              ) : (
                <p className="text-muted-foreground">Failed to load AI settings</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Behavior Tab - H4.3 Alert Threshold Configuration */}
        <TabsContent value="behavior" className="space-y-6 mt-6">
          <BehaviorThresholdsCard />
          <BehaviorFlagSettingsCard />
        </TabsContent>

        {/* Animal Profiles Tab - Gate S2 checklist definitions + S6 evaluation tracks */}
        {currentUser?.isAdmin && (
          <TabsContent value="animal-profiles" className="space-y-6 mt-6">
            <AnimalEvaluationTracksSettingsCard />
            <AnimalProfilesSettingsCard />
          </TabsContent>
        )}

        {/* Snapshots Tab - I6.3 Capacity Snapshot Schedule Settings + I16.3 Behavior Snapshot Schedule */}
        <TabsContent value="snapshots" className="space-y-6 mt-6">
          <CapacitySnapshotSettingsCard />
          <BehaviorSnapshotSettingsCard />
        </TabsContent>

        {currentUser?.isSuperAdmin && (
          <TabsContent value="hub-shadow" className="space-y-6 mt-6">
            <HubShadowResultsCard />
          </TabsContent>
        )}

        {/* Email Ingest Tab - I9.1.1 Inbound Email Ingestion Log */}
        <TabsContent value="email-ingest" className="space-y-6 mt-6">
          <EmailIngestTab />
        </TabsContent>

        {/* Security Tab - K2 Phase 2D MFA Settings */}
        <TabsContent value="security" className="space-y-6 mt-6">
          <MfaSettingsCard />
        </TabsContent>

        <TabsContent value="breeds" className="space-y-6 mt-6">
          <BreedMappingsCard />
        </TabsContent>
      </Tabs>

      <Sheet open={isZoneDrawerOpen} onOpenChange={setIsZoneDrawerOpen}>
        <SheetContent data-testid="drawer-zone">
          <SheetHeader>
            <SheetTitle>{editingZone ? "Edit Kennel" : "Add Kennel"}</SheetTitle>
            <SheetDescription>
              {editingZone
                ? "Update the kennel's label and capacity limits."
                : "Create a new kennel with capacity limits."}
            </SheetDescription>
          </SheetHeader>

          <Form {...zoneForm}>
            <form onSubmit={zoneForm.handleSubmit(onSubmitZone)} className="space-y-4 mt-6">
              <FormField
                control={zoneForm.control}
                name="zoneCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kennel Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={!!editingZone}
                        placeholder="SA CAT ADO"
                        maxLength={50}
                        data-testid="input-zone-code"
                      />
                    </FormControl>
                    <FormDescription>
                      Use uppercase codes like SA CAT ADO.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={zoneForm.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ""}>
                      <FormControl>
                        <SelectTrigger data-testid="select-location">
                          <SelectValue placeholder="Select a location" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {locationsList.filter(loc => loc.isActive).map((loc) => (
                          <SelectItem key={loc.id} value={loc.code} data-testid={`option-location-${loc.code.toLowerCase()}`}>
                            {loc.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={zoneForm.control}
                name="areaLabel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Area Label</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Salem Cat Adoption"
                        maxLength={200}
                        data-testid="input-area-label"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={zoneForm.control}
                  name="runs"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Runs</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          max={999}
                          data-testid="input-runs"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={zoneForm.control}
                  name="stainless"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stainless</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          max={999}
                          data-testid="input-stainless"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={zoneForm.control}
                  name="variety"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Variety</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          max={999}
                          data-testid="input-variety"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={zoneForm.control}
                  name="other"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Other</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          max={999}
                          data-testid="input-other"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label>Total Capacity</Label>
                <div
                  className="h-10 flex items-center px-3 border rounded-md bg-muted text-muted-foreground"
                  data-testid="text-total-capacity"
                >
                  {totalCapacity}
                </div>
                <p className="text-sm text-muted-foreground">
                  Calculated from runs + stainless + variety + other.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsZoneDrawerOpen(false)}
                  data-testid="button-cancel-zone"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createZoneMutation.isPending || updateZoneMutation.isPending}
                  data-testid="button-save-zone"
                >
                  {(createZoneMutation.isPending || updateZoneMutation.isPending) && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {editingZone ? "Save changes" : "Create Kennel"}
                </Button>
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deletingZone} onOpenChange={(open) => !open && setDeletingZone(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Kennel</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the kennel "{deletingZone?.zoneCode}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-zone">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingZone && deleteZoneMutation.mutate(deletingZone.id)}
              disabled={deleteZoneMutation.isPending}
              data-testid="button-confirm-delete-zone"
            >
              {deleteZoneMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isAliasDialogOpen} onOpenChange={setIsAliasDialogOpen}>
        <DialogContent data-testid="dialog-alias">
          <DialogHeader>
            <DialogTitle>Add Alias</DialogTitle>
            <DialogDescription>
              Create an alternate kennel code that maps to an existing kennel.
            </DialogDescription>
          </DialogHeader>

          <Form {...aliasForm}>
            <form onSubmit={aliasForm.handleSubmit(onSubmitAlias)} className="space-y-4">
              <FormField
                control={aliasForm.control}
                name="aliasCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Alias Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="SALEM CAT ADOPTION"
                        maxLength={50}
                        data-testid="input-alias-code"
                      />
                    </FormControl>
                    <FormDescription>
                      Use uppercase letters, numbers, and spaces only.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={aliasForm.control}
                name="zoneId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maps To</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-alias-zone">
                          <SelectValue placeholder="Select a kennel" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {zones.map((zone) => (
                          <SelectItem key={zone.id} value={zone.id}>
                            {zone.zoneCode} – {zone.areaLabel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsAliasDialogOpen(false);
                    aliasForm.reset();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createAliasMutation.isPending}
                  data-testid="button-save-alias"
                >
                  {createAliasMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Add Alias
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingAlias} onOpenChange={(open) => !open && setDeletingAlias(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Alias</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the alias "{deletingAlias?.aliasCode}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingAlias && deleteAliasMutation.mutate(deletingAlias.id)}
              disabled={deleteAliasMutation.isPending}
            >
              {deleteAliasMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isLocationDialogOpen} onOpenChange={setIsLocationDialogOpen}>
        <DialogContent data-testid="dialog-location">
          <DialogHeader>
            <DialogTitle>{editingLocation ? "Edit Location" : "Add Location"}</DialogTitle>
            <DialogDescription>
              {editingLocation
                ? "Update the location's label."
                : "Create a new shelter location."}
            </DialogDescription>
          </DialogHeader>

          <Form {...locationForm}>
            <form onSubmit={locationForm.handleSubmit(onSubmitLocation)} className="space-y-4">
              <FormField
                control={locationForm.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={!!editingLocation}
                        placeholder="SALEM"
                        className="font-mono uppercase"
                        maxLength={20}
                        data-testid="input-location-code"
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    </FormControl>
                    <FormDescription>
                      Use uppercase letters, numbers, and underscores only.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={locationForm.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Label</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Salem Adoption Center"
                        maxLength={100}
                        data-testid="input-location-label"
                      />
                    </FormControl>
                    <FormDescription>
                      The full name shown in the interface.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsLocationDialogOpen(false);
                    setEditingLocation(null);
                    locationForm.reset();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                  data-testid="button-save-location"
                >
                  {(createLocationMutation.isPending || updateLocationMutation.isPending) && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {editingLocation ? "Save Changes" : "Add Location"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deactivatingLocation} onOpenChange={(open) => !open && setDeactivatingLocation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate the location "{deactivatingLocation?.label}"?
              Deactivated locations cannot be assigned to new kennels.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deactivatingLocation && deactivateLocationMutation.mutate(deactivatingLocation.id)}
              disabled={deactivateLocationMutation.isPending}
            >
              {deactivateLocationMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
